// src/pages/Authentication/WorkerOnBoarding.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  Input,
  Label,
  FormFeedback,
  Alert,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import FormPhoneInput from "../../components/Common/FormPhoneInput";
import toastr from "toastr";
import withRouter from "../../components/Common/withRouter";
import { withTranslation } from "react-i18next";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import PropTypes from "prop-types";

// API hooks
import { useWorkerOnBoarding } from "../../api/auth";
import CustomHeader from "../../components/Auth/CustomHeader";
import { ProofOfAddressAcceptableContent } from "../../components/Common/ProofOfAddressAcceptableHint";
import { useGetAllSectorsNotPaginated } from "../../api/sector";
import { useGetAllCertsNotPaginated } from "../../api/certs";
import { toSentenceCase } from "../../utils/stringUtils";
import {
  getNationalityOptions,
  AVAILABILITY_OPTIONS,
  getLanguageOptions,
  TRANSPORT_OPTIONS,
  WHAT_MATTERS_MOST_OPTIONS,
  normalizeWhatMattersMost,
  PREFERRED_WORK_RADIUS_OPTIONS,
  FILE_VALIDATION,
  VALIDATION_PATTERNS,
  GENDER_OPTIONS,
  RIGHT_TO_WORK_STATUS_BRITISH_IRISH,
  RIGHT_TO_WORK_STATUS_OPTIONS_ALL,
  RIGHT_TO_WORK_STATUS_STUDENT_VISA,
  EMERGENCY_RELATIONSHIP_OPTIONS,
  STARTER_CHECKLIST_OPTIONS,
  STUDENT_LOAN_PLAN_OPTIONS,
  UK_HOME_OFFICE_SHARE_CODE_URL,
  UK_DVLA_SHARE_CODE_URL,
  UK_DBS_UPDATE_SERVICE_GOV_UK_URL,
} from "../../constants/workerOnboarding";
import { GOOGLE_MAP_KEY } from "../../environtments/environtment";

// File validation helper
const isUnder = (file, mb) =>
  file && file.size <= mb * FILE_VALIDATION.BYTES_IN_MB;

function isAcceptableOnScreeningVideoFile(file) {
  if (!(file instanceof File)) return false;
  if (file.type && file.type.startsWith("video/")) return true;
  return FILE_VALIDATION.ALLOWED_VIDEO_EXTENSIONS.test(file.name);
}

function pickScreeningRecorderMimeType() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return "";
  }
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

/** Parse Google Places Geocoder or Places Details `address_components` */
function parseGoogleAddressComponents(place) {
  if (!place?.address_components) return null;
  const comps = place.address_components;
  const get = (...types) => {
    const c = comps.find((x) => types.some((t) => x.types.includes(t)));
    return c?.long_name || "";
  };
  const streetNumber = get("street_number");
  const route = get("route");
  const line1 = [streetNumber, route].filter(Boolean).join(" ").trim();
  const subpremise = get("subpremise");
  const postcode = get("postal_code");
  const city =
    get("postal_town") || get("locality") || get("administrative_area_level_2");
  const county =
    get("administrative_area_level_2") || get("administrative_area_level_1");
  const country = get("country");
  const loc = place.geometry?.location;
  const lat =
    loc == null ? null : typeof loc.lat === "function" ? loc.lat() : loc.lat;
  const lng =
    loc == null ? null : typeof loc.lng === "function" ? loc.lng() : loc.lng;
  return {
    addressLine1: line1 || place.name || "",
    addressLine2: subpremise || "",
    postcode,
    city,
    county,
    country,
    formatted: place.formatted_address || "",
    lat:
      lat != null && typeof lat === "number"
        ? String(lat.toFixed(6))
        : lat != null
        ? String(lat)
        : "",
    lng:
      lng != null && typeof lng === "number"
        ? String(lng.toFixed(6))
        : lng != null
        ? String(lng)
        : "",
  };
}

const UK_COUNTRY = "United Kingdom";
const UK_ADDRESS_COUNTRY_OPTIONS = [{ value: UK_COUNTRY, label: UK_COUNTRY }];
const UK_ADDRESS_AUTOCOMPLETE_OPTIONS = {
  componentRestrictions: { country: "gb" },
};

function collectRoleRequiredCerts(sectorsData, sectorsForm, certsData) {
  const idSet = new Set();
  /** Embedded cert objects from sector API (role.certs) when not in certsData yet */
  const embeddedBySlug = new Map();

  (sectorsForm || []).forEach((row) => {
    const sec = sectorsData.find((s) => s.slug === row.sectorSlug);
    if (!sec?.roles) return;
    (row.roles || []).forEach((roleRef) => {
      const roleSlug =
        typeof roleRef === "string"
          ? roleRef
          : roleRef?.slug || roleRef?.value || roleRef?.roleSlug || "";
      const role = sec.roles.find((r) => r.slug === roleSlug);
      if (!role) return;

      (role.certIds || []).forEach((id) => {
        if (id != null) idSet.add(String(id));
      });

      (role.certs || []).forEach((c) => {
        if (c?.id != null) idSet.add(String(c.id));
        const emSlug = c?.slug;
        if (emSlug) embeddedBySlug.set(emSlug, c?.title || emSlug);
      });
    });
  });

  const list = [];
  const seenSlugs = new Set();

  idSet.forEach((id) => {
    const c = certsData.find((x) => String(x.id) === String(id));
    if (c?.slug && !seenSlugs.has(c.slug)) {
      seenSlugs.add(c.slug);
      list.push({ slug: c.slug, label: c.title || c.slug });
    }
  });

  embeddedBySlug.forEach((label, slug) => {
    if (seenSlugs.has(slug)) return;
    const meta = certsData.find((x) => x.slug === slug);
    seenSlugs.add(slug);
    list.push({ slug, label: meta?.title || label });
  });

  return list;
}

/** Yup `.test()` on an object — error is a string; field failures are a nested object. */
function isYupObjectLevelError(error) {
  return typeof error === "string";
}

function hasAnyDrivingDocValue(rightToWork = {}) {
  const dvlaShareCode = String(rightToWork?.dvlaShareCode || "").trim();
  const drivingLicenseExpiryDate = String(
    rightToWork?.drivingLicenseExpiryDate || ""
  ).trim();
  const licenseNumber = String(rightToWork?.licenseNumber || "").trim();
  const hasDrivingLicenseFile = rightToWork?.drivingLicense instanceof File;
  const hasDrivingLicenseBackFile =
    rightToWork?.drivingLicenseBack instanceof File;

  return Boolean(
    hasDrivingLicenseFile ||
      hasDrivingLicenseBackFile ||
      dvlaShareCode ||
      drivingLicenseExpiryDate ||
      licenseNumber
  );
}

function collectRolesRequiringDrivingDocs(sectorsData, sectorsForm) {
  const uniqueBySlug = new Map();

  (sectorsForm || []).forEach((row) => {
    const sec = sectorsData.find((s) => s.slug === row?.sectorSlug);
    if (!sec?.roles) return;

    (row?.roles || []).forEach((roleRef) => {
      const roleSlug =
        typeof roleRef === "string"
          ? roleRef
          : roleRef?.slug || roleRef?.value || roleRef?.roleSlug || "";
      if (!roleSlug) return;

      const role = sec.roles.find((r) => r.slug === roleSlug);
      if (!role?.drivingDocsRequired) return;

      if (!uniqueBySlug.has(role.slug)) {
        uniqueBySlug.set(role.slug, role.title || role.slug);
      }
    });
  });

  return Array.from(uniqueBySlug.entries()).map(([slug, label]) => ({
    slug,
    label,
  }));
}

function certificateEntriesSatisfySlug(entries, slug, certsData) {
  const certMeta = certsData.find((c) => c.slug === slug);
  if (!certMeta) return false;
  return (entries || []).some((entry) => {
    if (entry.certSlug !== slug) return false;
    const hasFile = !!(
      entry.file &&
      (entry.file instanceof File || entry.file?.name)
    );
    const hasSn = !!entry.serviceNumber?.trim?.();
    if (certMeta.hasServiceNumber) {
      return hasFile || hasSn;
    }
    return hasFile;
  });
}

const WorkerOnBoarding = (props) => {
  const { t } = props;
  document.title = t("title");

  const search = props?.router?.location?.search || "";
  const qp = useMemo(() => new URLSearchParams(search), [search]);
  const qpFullName = qp.get("fullName");
  const [qpFirstName, qpLastName] = qpFullName
    ? (() => {
        const parts = qpFullName.trim().split(/\s+/);
        return parts.length >= 2
          ? [parts[0], parts.slice(1).join(" ")]
          : [parts[0] || "", ""];
      })()
    : ["", ""];
  const qpEmail = qp.get("email");
  const qpPhone = (qp.get("phoneNumber") || "").replace(/^\+/, "");

  // API hooks
  const workerOnBoardingMutation = useWorkerOnBoarding();

  // Sectors + Roles (not paginated)
  const { data: sectorsDataRes, isLoading: sectorsLoading } =
    useGetAllSectorsNotPaginated();
  const sectorsData = useMemo(
    () => sectorsDataRes?.data || [],
    [sectorsDataRes]
  );

  // Certs (not paginated) - fetch automatically with useQuery
  const {
    mutate: loadCerts,
    data: certsDataRes,
    isPending: certsLoading,
  } = useGetAllCertsNotPaginated();
  const certsData = useMemo(() => {
    return (certsDataRes?.data?.data || certsDataRes?.data || [])
      .filter((cert) => cert.isActive)
      .sort((a, b) => {
        const aIsDbs = a?.hasServiceNumber ? 1 : 0;
        const bIsDbs = b?.hasServiceNumber ? 1 : 0;
        return bIsDbs - aIsDbs;
      });
  }, [certsDataRes]);

  useEffect(() => {
    // loadSectors();
    loadCerts();
  }, [loadCerts]);

  const [geoLoading, setGeoLoading] = useState(false);
  const [webcamModal, setWebcamModal] = useState(false);
  const [videoStream, setVideoStream] = useState(null);
  const videoRef = React.useRef(null);
  const addressAutocompleteRef = useRef(null);
  const complianceSectionRef = useRef(null);
  const roleCertRequiredFpRef = useRef("");
  const dismissedRoleCertFpRef = useRef("");
  const drivingDocsRequiredFpRef = useRef("");
  const dismissedDrivingDocsFpRef = useRef("");

  const [roleCertModalOpen, setRoleCertModalOpen] = useState(false);
  const [roleCertModalLabels, setRoleCertModalLabels] = useState([]);
  const [drivingDocsModalOpen, setDrivingDocsModalOpen] = useState(false);
  const [drivingDocsModalLabels, setDrivingDocsModalLabels] = useState([]);
  const [rtwIneligibleModalOpen, setRtwIneligibleModalOpen] = useState(false);

  const [screeningRecorderOpen, setScreeningRecorderOpen] = useState(false);
  const [screeningStream, setScreeningStream] = useState(null);
  const [screeningRecording, setScreeningRecording] = useState(false);
  const [screeningQuestionsHighContrast, setScreeningQuestionsHighContrast] =
    useState(true);
  const [onScreeningPreviewUrl, setOnScreeningPreviewUrl] = useState(null);

  const screeningRecordVideoRef = useRef(null);
  const screeningMediaRecorderRef = useRef(null);
  const screeningChunksRef = useRef([]);
  const screeningDiscardNextStopRef = useRef(false);
  const screeningStreamRef = useRef(null);

  const googleMapsLibraries = useMemo(() => ["places"], []);

  const { isLoaded: mapsLoaded } = useLoadScript({
    id: "worker-onboarding-google-maps",
    googleMapsApiKey: GOOGLE_MAP_KEY || "",
    libraries: googleMapsLibraries,
  });

  const reverseGeocodePlace = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&region=gb&key=${GOOGLE_MAP_KEY}`
      );
      const data = await response.json();
      if (data.status === "OK" && data.results?.length > 0) {
        const ukResult = data.results.find((result) =>
          result.address_components?.some(
            (component) =>
              component.types.includes("country") &&
              component.short_name === "GB"
          )
        );
        return ukResult || null;
      }
      return null;
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      return null;
    }
  };

  // Webcam functions
  const openWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" }, // Use front-facing camera
        audio: false,
      });
      setVideoStream(stream);
      setWebcamModal(true);
    } catch (error) {
      toastr.error(t("webcamAccessDenied"));
      console.error("Webcam access error:", error);
    }
  };

  // Update video stream when modal opens or stream changes
  useEffect(() => {
    if (webcamModal && videoStream) {
      let cleanupFunction = null;
      const currentVideoRef = videoRef.current;

      // Use setTimeout to ensure modal and video element are fully rendered
      const timer = setTimeout(() => {
        const videoElement = videoRef.current;
        if (videoElement) {
          videoElement.srcObject = videoStream;

          // Wait for video to be ready, then play
          const handleLoadedMetadata = () => {
            videoElement.play().catch((error) => {
              console.error("Error playing video:", error);
              toastr.error(t("webcamPlayError"));
            });
          };

          videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);

          // Also try to play immediately (some browsers don't fire loadedmetadata)
          videoElement.play().catch((error) => {
            console.error("Error playing video immediately:", error);
          });

          cleanupFunction = () => {
            videoElement.removeEventListener(
              "loadedmetadata",
              handleLoadedMetadata
            );
          };
        }
      }, 100);

      return () => {
        clearTimeout(timer);
        if (cleanupFunction) {
          cleanupFunction();
        }
        // Use the ref at cleanup time (it might have changed, but that's ok for cleanup)
        const videoElement = videoRef.current || currentVideoRef;
        if (videoElement) {
          videoElement.pause();
          videoElement.srcObject = null;
        }
      };
    }
  }, [webcamModal, videoStream, t]);

  const closeWebcam = () => {
    if (videoStream) {
      videoStream.getTracks().forEach((track) => track.stop());
      setVideoStream(null);
    }
    setWebcamModal(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const video = videoRef.current;

    // Check if video is ready
    if (
      video.readyState !== 4 ||
      video.videoWidth === 0 ||
      video.videoHeight === 0
    ) {
      toastr.error(t("webcamNotReady"));
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");

    // Flip the image horizontally to unmirror it (since we mirrored the display)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toastr.error(t("webcamCaptureFailed"));
          return;
        }

        // Convert blob to File object
        const file = new File([blob], `selfie-${Date.now()}.jpg`, {
          type: "image/jpeg",
          lastModified: Date.now(),
        });

        // Validate file size
        if (!isUnder(file, FILE_VALIDATION.SELFIE_MAX_MB)) {
          toastr.error(t("fileTooLarge", { n: FILE_VALIDATION.SELFIE_MAX_MB }));
          return;
        }

        // Set the file in the form
        handleFileChange(
          "personalInformation.jobarkIdSelfie",
          file,
          FILE_VALIDATION.SELFIE_MAX_MB
        );
        touchNested("personalInformation.jobarkIdSelfie");

        // Clear any existing error since we have a valid file
        formik.setFieldError("personalInformation.jobarkIdSelfie", undefined);

        // Close webcam
        closeWebcam();
        toastr.success(t("webcamPhotoCaptured"));
      },
      "image/jpeg",
      0.9
    ); // 90% quality
  };

  // Cleanup video stream on unmount
  useEffect(() => {
    return () => {
      if (videoStream) {
        videoStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [videoStream]);

  useEffect(() => {
    screeningStreamRef.current = screeningStream;
  }, [screeningStream]);

  useEffect(() => {
    if (screeningRecorderOpen && screeningStream) {
      let cleanupFunction = null;
      const currentVideoRef = screeningRecordVideoRef.current;
      const timer = setTimeout(() => {
        const videoElement = screeningRecordVideoRef.current;
        if (videoElement) {
          videoElement.srcObject = screeningStream;
          const handleLoadedMetadata = () => {
            videoElement.play().catch((error) => {
              console.error("Screening preview play error:", error);
              toastr.error(t("webcamPlayError"));
            });
          };
          videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
          videoElement.play().catch(() => {});
          cleanupFunction = () => {
            videoElement.removeEventListener(
              "loadedmetadata",
              handleLoadedMetadata
            );
          };
        }
      }, 100);
      return () => {
        clearTimeout(timer);
        if (cleanupFunction) cleanupFunction();
        const videoElement = screeningRecordVideoRef.current || currentVideoRef;
        if (videoElement) {
          videoElement.pause();
          videoElement.srcObject = null;
        }
      };
    }
  }, [screeningRecorderOpen, screeningStream, t]);

  useEffect(() => {
    return () => {
      const s = screeningStreamRef.current;
      if (s) {
        s.getTracks().forEach((track) => track.stop());
        screeningStreamRef.current = null;
      }
    };
  }, []);

  const nationalityOptions = useMemo(() => getNationalityOptions(), []);
  const addressCountryOptions = UK_ADDRESS_COUNTRY_OPTIONS;

  // Certificate entries: each row has certSlug (type), file (single), serviceNumber (for DBS)
  const nextCertEntryIdRef = useRef(1);
  const certFileInputRefsRef = useRef({});
  const defaultCertificateEntries = useMemo(
    () => [{ id: 1, certSlug: "", file: null, serviceNumber: "" }],
    []
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      role: "worker",

      personalInformation: {
        firstName: qpFirstName,
        lastName: qpLastName,
        dob: "",
        gender: "",
        addressSearch: "",
        addressLine1: "",
        addressLine2: "",
        postcode: "",
        city: "",
        county: "",
        country: UK_COUNTRY,
        languagesSpoken: [],
        phoneNumber: qpPhone,
        email: qpEmail,
        nationalInsuranceNumber: "",
        nationality: "",
        baseLat: "",
        baseLng: "",
        jobarkIdSelfie: null,
      },

      emergencyContact: {
        firstName: "",
        lastName: "",
        relationshipToWorker: "",
        phoneNumber: "",
        alternativePhoneNumber: "",
      },

      rightToWork: {
        holdsLegalRightToWorkUk: "",
        rightToWorkStatus: "",
        shareCode: "",
        visaExpiryDate: "",
        drivingLicense: null,
        drivingLicenseBack: null,
        dvlaShareCode: "",
        drivingLicenseExpiryDate: "",
        licenseNumber: "",
        uploadId: [],
        proofOfAddress: [],
        consentTrustID: false,
      },
      kycReport: null,

      employmentTax: {
        hasP45: "",
        p45Upload: null,
        starterChecklistPdf: null,
        starterChecklistChoice: "",
        hasPostgraduateLoan: "",
        planType: "",
        notes: "",
        acceptEmergencyTaxCode: false,
      },

      bankDetails: {
        accountHolderName: "",
        sortCode: "",
        accountNumber: "",
        bankName: "",
      },

      moreAboutYou: {
        healthAccessibilityDetails: "",
        hasNoConvictions: true,
        hasConvictionsOrPending: false,
        criminalConvictionsDetails: "",
        aboutYouWorkHistory: "",
      },

      pensionAutoEnrolment: null,

      workPreferences: {
        // dynamic list; first one required
        sectors: [{ sectorSlug: "", roles: [] }], // roles is array of role slugs
        preferredWorkRadius: "",
        availability: [],
        optOut48HourLimit: false,
        transport: [],
        whatMattersMost: [],
      },

      certificateEntries: defaultCertificateEntries,

      complianceSkills: {
        hasInformationTrue: false,
      },

      onScreeningVideo: null,
    },

    validationSchema: Yup.object({
      role: Yup.string().oneOf(["worker"]).required(),

      personalInformation: Yup.object({
        // fullName/sureName are built from firstName/lastName in API payload only; form uses firstName/lastName
        firstName: Yup.string().required(t("required")),
        lastName: Yup.string().required(t("required")),
        dob: Yup.string()
          .required(t("required"))
          .test("age", t("ageMustBe18"), function (value) {
            if (!value) return true; // Let required() handle empty values
            const birthDate = new Date(value);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (
              monthDiff < 0 ||
              (monthDiff === 0 && today.getDate() < birthDate.getDate())
            ) {
              age--;
            }
            return age >= 18;
          }),
        gender: Yup.string()
          .oneOf(Object.values(GENDER_OPTIONS), t("invalidGender"))
          .required(t("required")),
        addressLine1: Yup.string().trim().required(t("required")),
        addressLine2: Yup.string().trim().notRequired(),
        postcode: Yup.string().trim().required(t("required")),
        city: Yup.string().trim().required(t("required")),
        county: Yup.string().trim().notRequired(),
        country: Yup.string()
          .trim()
          .required(t("required"))
          .oneOf(
            [UK_COUNTRY],
            t("locationMustBeUk", "Address must be in the United Kingdom.")
          ),
        languagesSpoken: Yup.array()
          .of(Yup.string().trim())
          .min(1, t("selectAtLeastOne")),
        phoneNumber: Yup.string()
          .required(t("required"))
          .matches(/^[0-9]+$/, t("digitsOnly"))
          .min(10, t("minDigits", { n: 10 }))
          .max(15, t("maxDigits", { n: 15 })),
        email: Yup.string().email(t("emailInvalid")).required(t("required")),
        nationalInsuranceNumber: Yup.string()
          .transform((v) => (v == null ? "" : String(v).trim()))
          .test("ni-optional", t("niNumberInvalid"), (v) => {
            if (!v) return true;
            return VALIDATION_PATTERNS.NATIONAL_INSURANCE.test(
              String(v).replace(/\s/g, " ")
            );
          }),
        nationality: Yup.string().required(t("required")),
        baseLat: Yup.string().required(t("required")),
        baseLng: Yup.string().required(t("required")),
        jobarkIdSelfie: Yup.mixed()
          .required(t("uploadSelfieRequired"))
          .test(
            "fileRequired",
            t("uploadSelfieRequired"),
            (v) => v instanceof File
          )
          .test("f-type", t("fileTypeNotAllowed"), (v) =>
            v instanceof File
              ? FILE_VALIDATION.ALLOWED_IMAGE_EXTENSIONS.test(v.name)
              : false
          )
          .test(
            "f-size",
            t("fileTooLarge", { n: FILE_VALIDATION.SELFIE_MAX_MB }),
            (v) =>
              v instanceof File
                ? isUnder(v, FILE_VALIDATION.SELFIE_MAX_MB)
                : false
          ),
      }),

      emergencyContact: Yup.object({
        firstName: Yup.string().required(t("required")),
        lastName: Yup.string().required(t("required")),
        relationshipToWorker: Yup.string()
          .oneOf(
            EMERGENCY_RELATIONSHIP_OPTIONS.map((o) => o.value),
            t("required")
          )
          .required(t("required")),
        phoneNumber: Yup.string()
          .required(t("required"))
          .matches(/^[0-9]+$/, t("digitsOnly"))
          .min(10, t("minDigits", { n: 10 }))
          .max(15, t("maxDigits", { n: 15 })),
        alternativePhoneNumber: Yup.string()
          .nullable()
          .notRequired()
          .test("optional-phone", t("digitsOnly"), function (value) {
            if (!value || value.trim() === "") {
              return true;
            }
            const digitsOnly = value.replace(/[^0-9]/g, "");
            return (
              /^[0-9]+$/.test(digitsOnly) &&
              digitsOnly.length >= 10 &&
              digitsOnly.length <= 15
            );
          })
          .test(
            "diff-from-primary",
            t("emergencyAltPhoneMustDiffer"),
            function (value) {
              if (!value || String(value).trim() === "") return true;
              const primary = this.parent?.phoneNumber;
              if (primary == null || String(primary).trim() === "") return true;
              const altDigits = String(value).replace(/[^0-9]/g, "");
              const primaryDigits = String(primary).replace(/[^0-9]/g, "");
              if (!altDigits || !primaryDigits) return true;
              return altDigits !== primaryDigits;
            }
          ),
      }),

      rightToWork: Yup.object().shape({
        holdsLegalRightToWorkUk: Yup.string()
          .required(t("required"))
          .oneOf(["yes"], t("rtwLegalRightMustBeYes")),

        rightToWorkStatus: Yup.string().when("holdsLegalRightToWorkUk", {
          is: "yes",
          then: (schema) =>
            schema
              .required(t("required"))
              .oneOf(
                RIGHT_TO_WORK_STATUS_OPTIONS_ALL,
                t("invalidRightToWorkStatus")
              ),
          otherwise: (schema) => schema.notRequired(),
        }),

        shareCode: Yup.string().when(
          ["holdsLegalRightToWorkUk", "rightToWorkStatus"],
          {
            is: (holds, status) =>
              holds === "yes" &&
              status &&
              status !== RIGHT_TO_WORK_STATUS_BRITISH_IRISH,
            then: (schema) => schema.required(t("shareCodeRequired")),
            otherwise: (schema) => schema.notRequired(),
          }
        ),

        visaExpiryDate: Yup.string().when(
          ["holdsLegalRightToWorkUk", "rightToWorkStatus"],
          {
            is: (holds, status) =>
              holds === "yes" &&
              status &&
              status !== RIGHT_TO_WORK_STATUS_BRITISH_IRISH,
            then: (schema) => schema.required(t("required")),
            otherwise: (schema) => schema.notRequired(),
          }
        ),

        drivingLicense: Yup.mixed()
          .nullable()
          .test("driving-license-type", t("fileTypeNotAllowed"), (v) =>
            v == null
              ? true
              : v instanceof File
              ? FILE_VALIDATION.ALLOWED_EXTENSIONS.test(v.name)
              : false
          )
          .test(
            "driving-license-size",
            t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }),
            (v) =>
              v == null
                ? true
                : v instanceof File
                ? isUnder(v, FILE_VALIDATION.MAX_MB)
                : false
          )
          .test("driving-license-required", t("required"), function (value) {
            const anyDrivingDoc = hasAnyDrivingDocValue(this.parent || {});
            if (!anyDrivingDoc) return true;
            return value instanceof File;
          }),
        drivingLicenseBack: Yup.mixed()
          .nullable()
          .test("driving-license-back-type", t("fileTypeNotAllowed"), (v) =>
            v == null
              ? true
              : v instanceof File
              ? FILE_VALIDATION.ALLOWED_EXTENSIONS.test(v.name)
              : false
          )
          .test(
            "driving-license-back-size",
            t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }),
            (v) =>
              v == null
                ? true
                : v instanceof File
                ? isUnder(v, FILE_VALIDATION.MAX_MB)
                : false
          ),

        dvlaShareCode: Yup.string().test(
          "dvla-share-code-required",
          t("required"),
          function (value) {
            const anyDrivingDoc = hasAnyDrivingDocValue(this.parent || {});
            if (!anyDrivingDoc) return true;
            return !!String(value || "").trim();
          }
        ),

        drivingLicenseExpiryDate: Yup.string().test(
          "driving-license-expiry-required",
          t("required"),
          function (value) {
            const anyDrivingDoc = hasAnyDrivingDocValue(this.parent || {});
            if (!anyDrivingDoc) return true;
            return !!String(value || "").trim();
          }
        ),

        licenseNumber: Yup.string().test(
          "license-number-required",
          t("required"),
          function (value) {
            const anyDrivingDoc = hasAnyDrivingDocValue(this.parent || {});
            if (!anyDrivingDoc) return true;
            return !!String(value || "").trim();
          }
        ),

        uploadId: Yup.array()
          .of(
            Yup.mixed()
              .test("f-type", t("fileTypeNotAllowed"), (v) =>
                v instanceof File
                  ? FILE_VALIDATION.ALLOWED_EXTENSIONS.test(v.name)
                  : false
              )
              .test(
                "f-size",
                t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }),
                (v) =>
                  v instanceof File ? isUnder(v, FILE_VALIDATION.MAX_MB) : false
              )
          )
          .max(
            FILE_VALIDATION.MAX_FILES_PER_FIELD,
            t("maxFilesAllowed", { n: FILE_VALIDATION.MAX_FILES_PER_FIELD })
          )
          .when(["holdsLegalRightToWorkUk", "rightToWorkStatus"], {
            is: (holds, status) => holds === "yes" && Boolean(status),
            then: (schema) =>
              schema
                .min(1, t("uploadIdRequired"))
                .required(t("uploadIdRequired")),
            otherwise: (schema) => schema.nullable().notRequired(),
          }),

        proofOfAddress: Yup.array()
          .of(
            Yup.mixed()
              .test("f-type", t("fileTypeNotAllowed"), (v) =>
                v instanceof File
                  ? FILE_VALIDATION.ALLOWED_EXTENSIONS.test(v.name)
                  : false
              )
              .test(
                "f-size",
                t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }),
                (v) =>
                  v instanceof File ? isUnder(v, FILE_VALIDATION.MAX_MB) : false
              )
          )
          .max(
            FILE_VALIDATION.MAX_FILES_PER_FIELD,
            t("maxFilesAllowed", { n: FILE_VALIDATION.MAX_FILES_PER_FIELD })
          )
          .when(["holdsLegalRightToWorkUk", "rightToWorkStatus"], {
            is: (holds, status) => holds === "yes" && Boolean(status),
            then: (schema) =>
              schema
                .min(1, t("proofOfAddressRequired"))
                .required(t("proofOfAddressRequired")),
            otherwise: (schema) => schema.nullable().notRequired(),
          }),

        consentTrustID: Yup.boolean()
          .oneOf([true], t("consentRequired"))
          .required(t("consentRequired")),
      }),

      employmentTax: Yup.object().shape({
        hasP45: Yup.string()
          .oneOf(["yes", "no"], t("required"))
          .required(t("required")),
        p45Upload: Yup.mixed()
          .nullable()
          .when("hasP45", {
            is: "yes",
            then: (schema) =>
              schema
                .required(t("p45UploadRequired"))
                .test(
                  "fileRequired",
                  t("p45UploadRequired"),
                  (v) => v instanceof File
                )
                .test("f-type", t("fileTypeNotAllowed"), (v) =>
                  v instanceof File
                    ? FILE_VALIDATION.ALLOWED_EXTENSIONS.test(v.name)
                    : false
                )
                .test(
                  "f-size",
                  t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }),
                  (v) =>
                    v instanceof File
                      ? isUnder(v, FILE_VALIDATION.MAX_MB)
                      : false
                ),
            otherwise: (schema) => schema.nullable(),
          }),
        starterChecklistPdf: Yup.mixed()
          .nullable()
          .when("hasP45", {
            is: "no",
            then: (schema) => schema.nullable(),
            otherwise: (schema) => schema.nullable(),
          }),
        starterChecklistChoice: Yup.string().when("hasP45", {
          is: "no",
          then: (schema) =>
            schema
              .oneOf(
                STARTER_CHECKLIST_OPTIONS.map((opt) => opt.value),
                t("required")
              )
              .required(t("required")),
          otherwise: (schema) => schema.notRequired(),
        }),
        hasPostgraduateLoan: Yup.string().when("hasP45", {
          is: "no",
          then: (schema) =>
            schema.oneOf(["yes", "no"], t("required")).required(t("required")),
          otherwise: (schema) => schema.notRequired(),
        }),
        planType: Yup.string().when(["hasP45", "hasPostgraduateLoan"], {
          is: (hasP45, hasPostgraduateLoan) =>
            hasP45 === "no" && hasPostgraduateLoan === "yes",
          then: (schema) =>
            schema
              .oneOf(
                STUDENT_LOAN_PLAN_OPTIONS.map((opt) => opt.value),
                t("required")
              )
              .required(t("required")),
          otherwise: (schema) => schema.notRequired(),
        }),
        notes: Yup.string().notRequired(),
        acceptEmergencyTaxCode: Yup.boolean()
          .oneOf([true], t("required"))
          .required(t("required")),
      }),

      bankDetails: Yup.object({
        accountHolderName: Yup.string().required(t("required")),
        sortCode: Yup.string()
          .matches(VALIDATION_PATTERNS.SORT_CODE, t("sortCodeFormat"))
          .required(t("required")),
        accountNumber: Yup.string()
          .matches(VALIDATION_PATTERNS.ACCOUNT_NUMBER, t("numeric"))
          .min(7, t("minDigits", { n: 7 }))
          .required(t("required")),
        bankName: Yup.string().required(t("required")),
      }),

      moreAboutYou: Yup.object({
        healthAccessibilityDetails: Yup.string()
          .trim()
          .required(t("required"))
          .min(1, t("required")),
        hasNoConvictions: Yup.boolean(),
        hasConvictionsOrPending: Yup.boolean(),
        criminalConvictionsDetails: Yup.string().when(
          ["hasConvictionsOrPending"],
          {
            is: (hasConvictionsOrPending) => hasConvictionsOrPending === true,
            then: (schema) => schema.trim().required(t("required")),
            otherwise: (schema) => schema.notRequired().nullable(),
          }
        ),
        aboutYouWorkHistory: Yup.string().trim().required(t("required")),
      }).test(
        "criminal-convictions-choice",
        t("moreAboutYouConvictionsRequired") ||
          "Please confirm whether you have any unspent convictions or pending cases.",
        (obj) =>
          obj?.hasNoConvictions === true ||
          obj?.hasConvictionsOrPending === true
      ),

      pensionAutoEnrolment: Yup.boolean()
        .nullable()
        .test(
          "pension-preference",
          t("pensionPreferenceRequired") ||
            "Please confirm your pension preference (remain enrolled or opt out).",
          (val) => val === true || val === false
        ),

      workPreferences: Yup.object({
        sectors: Yup.array()
          .of(
            Yup.object({
              sectorSlug: Yup.string().required(t("required")),
              roles: Yup.array()
                .of(Yup.string().trim().min(1))
                .min(1, t("required")),
            })
          )
          .min(1, t("addAtLeastOne"))
          .test("unique-sectors", t("duplicateSectorNotAllowed"), (arr) => {
            const slugs = (arr || []).map((s) => s?.sectorSlug).filter(Boolean);
            return slugs.length === new Set(slugs).size;
          }),
        preferredWorkRadius: Yup.number()
          .typeError(t("numeric"))
          .oneOf(PREFERRED_WORK_RADIUS_OPTIONS, t("preferredRadiusAllowed"))
          .required(t("required")),
        availability: Yup.array()
          .of(Yup.string())
          .min(1, t("selectAtLeastOne")),
        transport: Yup.array().of(Yup.string()).min(1, t("selectAtLeastOne")),
        optOut48HourLimit: Yup.boolean().required(),
        whatMattersMost: Yup.array()
          .of(Yup.string())
          .min(1, t("selectAtLeastOne"))
          .required(t("required")),
      }),

      certificateEntries: Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.number().required(),
            certSlug: Yup.string(),
            file: Yup.mixed().nullable(),
            serviceNumber: Yup.string(),
          })
        )
        .nullable(),

      complianceSkills: Yup.object().shape({
        hasInformationTrue: Yup.boolean()
          .oneOf(
            [true],
            t("informationTrueRequired") ||
              "You must confirm the information is true and accurate"
          )
          .required(t("required")),
      }),

      onScreeningVideo: Yup.mixed()
        .required(t("onScreeningVideoRequired"))
        .test(
          "onScreeningVideoFile",
          t("onScreeningVideoRequired"),
          (v) => v instanceof File
        )
        .test("onScreeningVideoType", t("onScreeningVideoInvalidType"), (v) =>
          v instanceof File ? isAcceptableOnScreeningVideoFile(v) : false
        )
        .test(
          "onScreeningVideoSize",
          t("onScreeningVideoTooLarge", {
            n: FILE_VALIDATION.ONSCREENING_VIDEO_MAX_MB,
          }),
          (v) =>
            v instanceof File
              ? isUnder(v, FILE_VALIDATION.ONSCREENING_VIDEO_MAX_MB)
              : false
        ),

      kycReport: Yup.mixed()
        .nullable()
        .test("kyc-report-type", t("fileTypeNotAllowed"), (v) =>
          v == null
            ? true
            : v instanceof File
            ? FILE_VALIDATION.ALLOWED_EXTENSIONS.test(v.name)
            : false
        )
        .test(
          "kyc-report-size",
          t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }),
          (v) =>
            v == null
              ? true
              : v instanceof File
              ? isUnder(v, FILE_VALIDATION.MAX_MB)
              : false
        ),
    }),

    onSubmit: async (values, { setFieldError, setFieldTouched }) => {
      // Validate certificate entries (file required) using current values - Yup may not see File objects
      console.log(
        "[WorkerOnBoarding] onSubmit ran — validation passed, form is submitting"
      );
      const entries = values.certificateEntries || [];
      const certErrors = [];
      let hasCertError = false;
      entries.forEach((entry, idx) => {
        if (!entry?.certSlug) {
          certErrors[idx] = null;
          return;
        }
        const cert = certsData.find((c) => c.slug === entry.certSlug);
        if (!cert) {
          certErrors[idx] = null;
          return;
        }
        const hasFile = !!(
          entry?.file &&
          (entry.file instanceof File || entry.file?.name)
        );
        const hasServiceNumber = !!entry?.serviceNumber?.trim?.();
        if (cert.hasServiceNumber) {
          if (!hasFile && !hasServiceNumber) {
            certErrors[idx] = {
              message:
                t("certFileOrServiceNumberRequired") ||
                "Please upload a certificate file and/or enter the DBS service number.",
            };
            hasCertError = true;
          } else {
            certErrors[idx] = null;
          }
          return;
        }
        if (!hasFile) {
          certErrors[idx] = {
            message:
              t("selectFileForCertificate") ||
              "Please select a file for this certificate type.",
          };
          hasCertError = true;
        } else {
          certErrors[idx] = null;
        }
      });
      if (hasCertError) {
        setFieldError("certificateEntries", certErrors);
        setFieldTouched("certificateEntries", true);
        return;
      }

      try {
        const fd = buildOnboardingFormData(values);
        const apiRes = await workerOnBoardingMutation.mutateAsync(fd);
        props.router.navigate(
          `/verify-otp?email=${values.personalInformation.email}&role=${values.role}&token=${apiRes.token}`
        );
        toastr.success(t("success"));
      } catch {
        toastr.error(t("tryAgain"));
      }
    },
  });

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!formik.dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [formik.dirty]);

  // ---------- helpers ----------
  const setNested = (path, val) => formik.setFieldValue(path, val);
  const touchNested = (path) => formik.setFieldTouched(path, true);

  const setPensionChoice = (choice) => {
    formik.setFieldValue("pensionAutoEnrolment", choice === "remain", true);
    formik.setFieldTouched("pensionAutoEnrolment", true, false);
  };

  const setConvictionsChoice = async (choice) => {
    const nextMoreAboutYou = {
      ...formik.values.moreAboutYou,
      ...(choice === "none"
        ? {
            hasNoConvictions: true,
            hasConvictionsOrPending: false,
            criminalConvictionsDetails: "",
          }
        : {
            hasConvictionsOrPending: true,
            hasNoConvictions: false,
          }),
    };
    await formik.setFieldValue("moreAboutYou", nextMoreAboutYou, false);
    formik.setFieldTouched(
      choice === "none"
        ? "moreAboutYou.hasNoConvictions"
        : "moreAboutYou.hasConvictionsOrPending",
      true,
      false
    );
    if (isYupObjectLevelError(formik.errors.moreAboutYou)) {
      formik.setFieldError("moreAboutYou", undefined);
    }
  };

  const moreAboutYouFieldShowError = (field) =>
    (!!formik.touched.moreAboutYou?.[field] || formik.submitCount > 0) &&
    !!formik.errors.moreAboutYou?.[field];

  const pensionChoiceError =
    typeof formik.errors.pensionAutoEnrolment === "string"
      ? formik.errors.pensionAutoEnrolment
      : "";
  const pensionRadiosShowError =
    !!pensionChoiceError &&
    (!!formik.touched.pensionAutoEnrolment || formik.submitCount > 0);

  const convictionsChoiceError = isYupObjectLevelError(
    formik.errors.moreAboutYou
  )
    ? formik.errors.moreAboutYou
    : "";
  const convictionsRadiosShowError =
    !!convictionsChoiceError &&
    (!!formik.touched.moreAboutYou?.hasNoConvictions ||
      !!formik.touched.moreAboutYou?.hasConvictionsOrPending ||
      formik.submitCount > 0);
  const setOnScreeningVideoValue = async (fileOrNull) => {
    await formik.setFieldValue("onScreeningVideo", fileOrNull, true);
    await formik.setFieldTouched("onScreeningVideo", true, false);
  };

  const openScreeningRecorder = async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) {
      toastr.error(t("webcamAccessDenied"));
      return;
    }
    try {
      screeningDiscardNextStopRef.current = false;
      setScreeningQuestionsHighContrast(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: true,
      });
      screeningStreamRef.current = stream;
      setScreeningStream(stream);
      setScreeningRecording(false);
      setScreeningRecorderOpen(true);
    } catch (err) {
      console.error(err);
      toastr.error(t("webcamAccessDenied"));
    }
  };

  const finishScreeningRecorderUiCleanup = () => {
    screeningMediaRecorderRef.current = null;
    screeningChunksRef.current = [];
    const s = screeningStreamRef.current;
    if (s) {
      s.getTracks().forEach((track) => track.stop());
      screeningStreamRef.current = null;
    }
    setScreeningStream(null);
    setScreeningRecording(false);
    setScreeningRecorderOpen(false);
  };

  const startScreeningRecording = () => {
    const stream = screeningStreamRef.current;
    if (!stream || typeof MediaRecorder === "undefined") {
      toastr.error(t("onScreeningRecorderNotSupported"));
      return;
    }
    screeningChunksRef.current = [];
    screeningDiscardNextStopRef.current = false;
    const mimeType = pickScreeningRecorderMimeType();
    let mr;
    try {
      mr = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch (e) {
      console.error(e);
      toastr.error(t("onScreeningRecorderNotSupported"));
      return;
    }
    screeningMediaRecorderRef.current = mr;
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        screeningChunksRef.current.push(e.data);
      }
    };
    mr.onstop = () => {
      const discard = screeningDiscardNextStopRef.current;
      screeningDiscardNextStopRef.current = false;
      if (!discard) {
        const chunks = screeningChunksRef.current;
        const outType = mr.mimeType || "video/webm";
        const blob = new Blob(chunks, { type: outType });
        const ext = outType.includes("mp4") ? "mp4" : "webm";
        const file = new File([blob], `onscreening-${Date.now()}.${ext}`, {
          type: outType || "video/webm",
          lastModified: Date.now(),
        });
        if (!isUnder(file, FILE_VALIDATION.ONSCREENING_VIDEO_MAX_MB)) {
          toastr.error(
            t("onScreeningVideoTooLarge", {
              n: FILE_VALIDATION.ONSCREENING_VIDEO_MAX_MB,
            })
          );
        } else {
          setOnScreeningVideoValue(file);
          toastr.success(t("onScreeningVideoSaved"));
        }
      }
      finishScreeningRecorderUiCleanup();
    };
    try {
      mr.start(250);
      setScreeningRecording(true);
    } catch (e) {
      console.error(e);
      screeningMediaRecorderRef.current = null;
      toastr.error(t("onScreeningRecorderNotSupported"));
    }
  };

  const stopScreeningRecording = () => {
    screeningDiscardNextStopRef.current = false;
    const mr = screeningMediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.stop();
    }
  };

  const cancelScreeningRecorder = () => {
    screeningDiscardNextStopRef.current = true;
    const mr = screeningMediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.stop();
    } else {
      screeningDiscardNextStopRef.current = false;
      finishScreeningRecorderUiCleanup();
    }
  };

  useEffect(() => {
    const f = formik.values.onScreeningVideo;
    let url = null;
    if (f instanceof File) {
      url = URL.createObjectURL(f);
      setOnScreeningPreviewUrl(url);
    } else {
      setOnScreeningPreviewUrl(null);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [formik.values.onScreeningVideo]);

  const requestLocation = () => {
    if (!("geolocation" in navigator)) {
      toastr.error(t("geolocationNotSupported"));
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const latStr = String(latitude.toFixed(6));
        const lngStr = String(longitude.toFixed(6));
        setNested("personalInformation.baseLat", latStr);
        setNested("personalInformation.baseLng", lngStr);
        const place = await reverseGeocodePlace(latitude, longitude);
        if (!place) {
          setGeoLoading(false);
          toastr.error(
            t(
              "locationMustBeUk",
              "Your location must be in the United Kingdom."
            )
          );
          return;
        }
        const parsed = parseGoogleAddressComponents(place);
        if (parsed) {
          if (parsed.addressLine1)
            setNested("personalInformation.addressLine1", parsed.addressLine1);
          if (parsed.addressLine2)
            setNested("personalInformation.addressLine2", parsed.addressLine2);
          if (parsed.postcode)
            setNested("personalInformation.postcode", parsed.postcode);
          if (parsed.city) setNested("personalInformation.city", parsed.city);
          if (parsed.county)
            setNested("personalInformation.county", parsed.county);
          setNested("personalInformation.country", UK_COUNTRY);
          if (parsed.formatted)
            setNested("personalInformation.addressSearch", parsed.formatted);
        }
        setGeoLoading(false);
        toastr.success(t("useMyLocationSuccess"));
      },
      (err) => {
        setGeoLoading(false);
        let message;
        if (err?.code === 1) {
          message = t("geolocationPermissionDenied");
        } else if (err?.code === 3) {
          message = t("geolocationTimeout");
        } else {
          message = t("geolocationFailed");
        }
        toastr.error(message);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const onLoadAddressAutocomplete = (ac) => {
    addressAutocompleteRef.current = ac;
  };

  const onAddressPlaceChanged = () => {
    const ac = addressAutocompleteRef.current;
    if (!ac) return;
    const place = ac.getPlace();
    if (!place?.address_components) return;
    const parsed = parseGoogleAddressComponents(place);
    if (!parsed) return;
    if (parsed.addressLine1)
      setNested("personalInformation.addressLine1", parsed.addressLine1);
    if (parsed.addressLine2)
      setNested("personalInformation.addressLine2", parsed.addressLine2);
    if (parsed.postcode)
      setNested("personalInformation.postcode", parsed.postcode);
    if (parsed.city) setNested("personalInformation.city", parsed.city);
    if (parsed.county) setNested("personalInformation.county", parsed.county);
    setNested("personalInformation.country", UK_COUNTRY);
    if (parsed.lat) setNested("personalInformation.baseLat", parsed.lat);
    if (parsed.lng) setNested("personalInformation.baseLng", parsed.lng);
    if (parsed.formatted)
      setNested("personalInformation.addressSearch", parsed.formatted);
    touchNested("personalInformation.addressLine1");
    touchNested("personalInformation.postcode");
    touchNested("personalInformation.city");
    touchNested("personalInformation.country");
  };

  const missingRoleRequiredCerts = useMemo(() => {
    const requiredList = collectRoleRequiredCerts(
      sectorsData,
      formik.values.workPreferences?.sectors,
      certsData
    );
    return requiredList.filter(
      (r) =>
        !certificateEntriesSatisfySlug(
          formik.values.certificateEntries,
          r.slug,
          certsData
        )
    );
  }, [
    sectorsData,
    certsData,
    formik.values.workPreferences?.sectors,
    formik.values.certificateEntries,
  ]);

  useEffect(() => {
    const sectors = formik.values.workPreferences?.sectors;
    const entries = formik.values.certificateEntries;

    const requiredList = collectRoleRequiredCerts(
      sectorsData,
      sectors,
      certsData
    );
    const requiredFp = requiredList
      .map((x) => x.slug)
      .sort()
      .join("|");
    if (!requiredFp) {
      setRoleCertModalOpen(false);
      return;
    }
    const missing = requiredList.filter(
      (r) => !certificateEntriesSatisfySlug(entries, r.slug, certsData)
    );
    if (missing.length === 0) {
      setRoleCertModalOpen(false);
      return;
    }
    if (dismissedRoleCertFpRef.current === requiredFp) return;
    roleCertRequiredFpRef.current = requiredFp;
    setRoleCertModalLabels(missing.map((m) => m.label));
    setRoleCertModalOpen(true);
  }, [
    sectorsData,
    certsData,
    formik.values.workPreferences?.sectors,
    formik.values.certificateEntries,
  ]);

  useEffect(() => {
    const requiredRoles = collectRolesRequiringDrivingDocs(
      sectorsData,
      formik.values.workPreferences?.sectors
    );
    const requiredFp = requiredRoles
      .map((x) => x.slug)
      .sort()
      .join("|");

    if (!requiredFp) {
      setDrivingDocsModalOpen(false);
      return;
    }

    if (dismissedDrivingDocsFpRef.current === requiredFp) return;

    drivingDocsRequiredFpRef.current = requiredFp;
    setDrivingDocsModalLabels(requiredRoles.map((r) => r.label));
    setDrivingDocsModalOpen(true);
  }, [sectorsData, formik.values.workPreferences?.sectors]);

  // Helper to get the first error field path
  const getFirstErrorField = (errors) => {
    const visit = (val, path = "") => {
      if (!val) return null;

      // Most common: Yup returns strings for leaf errors
      if (typeof val === "string") return path;

      // Arrays: Yup/Formik errors for field arrays
      if (Array.isArray(val)) {
        for (let i = 0; i < val.length; i++) {
          const nextPath = path ? `${path}[${i}]` : `[${i}]`;
          const found = visit(val[i], nextPath);
          if (found) return found;
        }
        return null;
      }

      // Objects: recurse keys
      if (typeof val === "object") {
        for (const key of Object.keys(val)) {
          const nextPath = path ? `${path}.${key}` : key;
          const found = visit(val[key], nextPath);
          if (found) return found;
        }
        return null;
      }

      // Fallback
      return path || null;
    };

    return visit(errors, "");
  };

  // Track last submit count to prevent scrolling during typing
  const lastSubmitCountRef = React.useRef(0);

  // Scroll to first error field on form submission with errors
  useEffect(() => {
    // Only scroll if submitCount actually increased (new submit attempt)
    // and user is not currently typing in an input field
    const isUserTyping =
      document.activeElement?.tagName === "INPUT" ||
      document.activeElement?.tagName === "TEXTAREA" ||
      document.activeElement?.tagName === "SELECT";

    const submitCountIncreased =
      formik.submitCount > lastSubmitCountRef.current;

    if (
      submitCountIncreased &&
      Object.keys(formik.errors).length > 0 &&
      !isUserTyping
    ) {
      lastSubmitCountRef.current = formik.submitCount;

      // Prefer DOM-based "top-most invalid" for accurate UX (matches what user sees)
      const pickTopMost = (nodes) => {
        const els = Array.from(nodes || []).filter((el) => {
          // only pick visible elements
          if (!el) return false;
          if (el.getAttribute && el.getAttribute("aria-hidden") === "true")
            return false;
          return !!(
            el.offsetWidth ||
            el.offsetHeight ||
            el.getClientRects().length
          );
        });
        if (!els.length) return null;
        let best = els[0];
        let bestTop = best.getBoundingClientRect().top;
        for (const el of els) {
          const top = el.getBoundingClientRect().top;
          if (top < bestTop) {
            bestTop = top;
            best = el;
          }
        }
        return best;
      };

      setTimeout(() => {
        // Don't scroll if user started typing after the submit
        if (
          document.activeElement?.tagName === "INPUT" ||
          document.activeElement?.tagName === "TEXTAREA" ||
          document.activeElement?.tagName === "SELECT"
        ) {
          return;
        }

        // 1) Find the top-most invalid input/select/textarea
        const invalidInputs = document.querySelectorAll(
          "input.is-invalid, select.is-invalid, textarea.is-invalid, .react-tel-input input.is-invalid"
        );
        let element = pickTopMost(invalidInputs);

        // 2) Fallback: top-most visible invalid feedback block (checkboxes etc.)
        if (!element) {
          const invalidFeedback = document.querySelectorAll(
            ".invalid-feedback.d-block"
          );
          element = pickTopMost(invalidFeedback);
        }

        // 3) Fallback: old path-based method (best effort)
        if (!element) {
          const firstErrorPath = getFirstErrorField(formik.errors);
          if (firstErrorPath) {
            const dotPath = firstErrorPath.replace(/\[(\d+)\]/g, ".$1");
            const nameCandidates = [firstErrorPath, dotPath];
            for (const p of nameCandidates) {
              const found = document.querySelector(`[name="${p}"]`);
              if (found) {
                element = found;
                break;
              }
            }
          }
        }

        if (element) {
          const card = element.closest(".card");
          const scrollTarget = card || element;
          scrollTarget.scrollIntoView({ behavior: "smooth", block: "start" });

          // Focus (only if it's an input-like element)
          if (element.focus && typeof element.focus === "function") {
            setTimeout(() => element.focus(), 150);
          } else {
            const focusable = element.querySelector?.(
              "input, select, textarea"
            );
            if (focusable?.focus) setTimeout(() => focusable.focus(), 150);
          }
          return;
        }

        // Final fallback: scroll to top of form
        const formElement = document.querySelector("form");
        if (formElement)
          formElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } else if (submitCountIncreased) {
      // Update ref even if no errors, so we track submit attempts correctly
      lastSubmitCountRef.current = formik.submitCount;
    }
  }, [formik.submitCount, formik.errors, formik.touched]);

  // robust checkbox setter (prevents flakiness)
  const setChecked = (path) => (e) => setNested(path, !!e.target.checked);

  // file change with size + ext validation
  // SINGLE file (selfie)
  const handleFileChange = (path, file, maxMB) => {
    if (file && !isUnder(file, maxMB)) {
      toastr.error(t("fileTooLarge", { n: maxMB }));
      setNested(path, null);
      return;
    }
    if (file && !FILE_VALIDATION.ALLOWED_EXTENSIONS.test(file.name)) {
      toastr.error(t("fileTypeNotAllowed"));
      setNested(path, null);
      return;
    }
    setNested(path, file || null);
  };

  const handleFilesChange = (path, files, inputEl) => {
    let maxCount = FILE_VALIDATION.MAX_FILES_PER_FIELD;
    let maxMB = FILE_VALIDATION.MAX_MB;
    const list = Array.from(files || []);

    if (!list.length) {
      setNested(path, []);
      return;
    }
    if (list.length > maxCount) {
      toastr.error(t("uploadUpToFiles", { n: maxCount }));
      setNested(path, []);
      if (inputEl) inputEl.value = ""; // clear input field
      return;
    }
    for (const f of list) {
      if (!isUnder(f, maxMB)) {
        toastr.error(t("fileTooLargeEach", { name: f.name, n: maxMB }));
        setNested(path, []);
        if (inputEl) inputEl.value = "";
        return;
      }
      if (!FILE_VALIDATION.ALLOWED_EXTENSIONS.test(f.name)) {
        toastr.error(t("fileNameTypeNotAllowed", { name: f.name }));
        setNested(path, []);
        if (inputEl) inputEl.value = "";
        return;
      }
    }
    setNested(path, list);
  };

  const addCertificateEntry = () => {
    const list = [...(formik.values.certificateEntries || [])];
    list.push({
      id: nextCertEntryIdRef.current++,
      certSlug: "",
      file: null,
      serviceNumber: "",
    });
    formik.setFieldValue("certificateEntries", list);
  };

  const removeCertificateEntry = (index) => {
    const list = [...(formik.values.certificateEntries || [])];
    list.splice(index, 1);
    formik.setFieldValue(
      "certificateEntries",
      list.length
        ? list
        : [
            {
              id: nextCertEntryIdRef.current++,
              certSlug: "",
              file: null,
              serviceNumber: "",
            },
          ]
    );
  };

  const setCertificateEntryField = (index, field, value) => {
    const list = [...(formik.values.certificateEntries || [])];
    if (!list[index]) return;
    list[index] = { ...list[index], [field]: value };
    formik.setFieldValue("certificateEntries", list);
  };

  const handleCertificateFile = (index, file, inputEl) => {
    if (file && !isUnder(file, FILE_VALIDATION.MAX_MB)) {
      toastr.error(t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }));
      if (inputEl) inputEl.value = "";
      return;
    }
    if (file && !FILE_VALIDATION.ALLOWED_EXTENSIONS.test(file.name)) {
      toastr.error(t("fileTypeNotAllowed"));
      if (inputEl) inputEl.value = "";
      return;
    }
    setCertificateEntryField(index, "file", file || null);
  };

  const handleDbsCertificateServiceNumberChange = (index, _entryId, value) => {
    setCertificateEntryField(index, "serviceNumber", value);
  };

  const clearCertificateEntryFile = (index, entryId) => {
    setCertificateEntryField(index, "file", null);
    if (entryId != null) {
      const input = certFileInputRefsRef.current[entryId];
      if (input) input.value = "";
    }
  };

  // Auto-format sort code to UK format (00-00-00)
  const handleSortCodeChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, ""); // Remove non-digits
    if (value.length > 6) value = value.slice(0, 6); // Max 6 digits

    // Add hyphens automatically
    if (value.length > 4) {
      value = `${value.slice(0, 2)}-${value.slice(2, 4)}-${value.slice(4)}`;
    } else if (value.length > 2) {
      value = `${value.slice(0, 2)}-${value.slice(2)}`;
    }

    setNested("bankDetails.sortCode", value);
  };

  const buildOnboardingFormData = (values) => {
    const fd = new FormData();

    // 1) simple fields
    fd.append("role", values.role || "worker");

    // 2) JSON parts (send as plain strings, not Blobs)
    const normalizePlus = (v) => (v ? `+${String(v).replace(/^\+/, "")}` : "");

    const pi = values.personalInformation || {};
    const line1 = (pi.addressLine1 || "").trim();
    const line2 = (pi.addressLine2 || "").trim();
    const city = (pi.city || "").trim();
    const county = (pi.county || "").trim();
    const pc = (pi.postcode || "").trim();
    const country = (pi.country || "").trim();
    const tail = [city, county, pc, country].filter(Boolean).join(", ");
    const currentAddressCombined =
      [line1, line2, tail].filter(Boolean).join(", ") || "N/A";

    const personalInformation = {
      firstName: pi.firstName ?? "",
      surname: pi.lastName ?? "",
      fullName: pi.firstName ?? "",
      dob: pi.dob ?? "",
      gender: pi.gender ?? "",
      currentAddress: currentAddressCombined,
      road: line1 || "N/A",
      door: line2 || "N/A",
      addressLine1: line1 || "N/A",
      addressLine2: line2 || "N/A",
      city: city || "N/A",
      county: county || "",
      postalCode: pc || "N/A",
      country: country || "N/A",
      phoneNumber: normalizePlus(pi.phoneNumber),
      email: pi.email ?? "",
      nationalInsuranceNumber: (pi.nationalInsuranceNumber || "").trim(),
      nationality: pi.nationality ?? "",
      baseLat: pi.baseLat ?? "",
      baseLng: pi.baseLng ?? "",
    };

    const ec = values.emergencyContact || {};
    const emergencyContact = {
      fullName: ec.firstName ?? "",
      surname: ec.lastName ?? "",
      relationshipToWorker: ec.relationshipToWorker || "",
      phoneNumber: normalizePlus(ec.phoneNumber),
      ...(ec.alternativePhoneNumber?.trim?.()
        ? {
            alternativePhoneNumber: normalizePlus(ec.alternativePhoneNumber),
          }
        : {}),
    };

    const rtwVals = values?.rightToWork || {};
    const rightToWorkStatus = rtwVals.rightToWorkStatus || "";
    const isBritishOrIrish =
      rightToWorkStatus === RIGHT_TO_WORK_STATUS_BRITISH_IRISH;
    const rightToWork = {
      rightToWorkInUk: true,
      isBritishOrIrish,
      rightToWorkStatus,
      homeShareSortCode: isBritishOrIrish ? "" : rtwVals.shareCode || "",
      visaExpiryDate: isBritishOrIrish ? "" : rtwVals.visaExpiryDate || "",
      dvlaShareCode: rtwVals.dvlaShareCode || "",
      drivingLicenseExpiryDate: rtwVals.drivingLicenseExpiryDate || "",
      licenseNumber: rtwVals.licenseNumber || "",
      consentTrustID: !!rtwVals.consentTrustID,
    };

    // Normalize roles to { roleSlug, isEligible }
    const mapRoleSlug = (r) => {
      if (!r) return null;
      if (typeof r === "string") return r;
      return r.roleSlug || r.value || r.slug || r.id || String(r);
    };

    const whatMattersMost = normalizeWhatMattersMost(
      values?.workPreferences?.whatMattersMost
    );

    const langs = values?.personalInformation?.languagesSpoken;
    const workPreferences = {
      ...values.workPreferences,
      languages: Array.isArray(langs) ? langs : [],
      preferredWorkRadius: Number(
        values?.workPreferences?.preferredWorkRadius ?? 0
      ),
      whatMattersMost,
      sectors: (values?.workPreferences?.sectors || []).map((sec) => ({
        sectorSlug: sec?.sectorSlug || sec?.value || sec?.slug || String(sec),
        roles: (sec?.roles || [])
          .map(mapRoleSlug)
          .filter(Boolean)
          .map((roleSlug) => ({ roleSlug, isEligible: false })),
      })),
    };

    fd.append("personalInformation", JSON.stringify(personalInformation));
    fd.append("emergencyContact", JSON.stringify(emergencyContact));
    fd.append("rightToWork", JSON.stringify(rightToWork));
    const employmentTaxData = {
      hasP45: values?.employmentTax?.hasP45 === "yes",
      starterChecklistChoice:
        values?.employmentTax?.hasP45 === "no"
          ? values?.employmentTax?.starterChecklistChoice || ""
          : "",
      hasPostgraduateLoan:
        values?.employmentTax?.hasP45 === "no"
          ? values?.employmentTax?.hasPostgraduateLoan === "yes"
          : false,
      planType:
        values?.employmentTax?.hasP45 === "no" &&
        values?.employmentTax?.hasPostgraduateLoan === "yes"
          ? values?.employmentTax?.planType || ""
          : "",
      notesField: (values?.employmentTax?.notes || "").trim(),
      pensionAutoEnrolment: values.pensionAutoEnrolment === true,
    };
    fd.append("employmentTax", JSON.stringify(employmentTaxData));
    fd.append("bankDetails", JSON.stringify(values.bankDetails || {}));
    fd.append(
      "moreAboutYou",
      JSON.stringify({
        healthAccessibilityAdjustments:
          values?.moreAboutYou?.healthAccessibilityDetails?.trim() || "",
        aboutYouAndWorkHistory:
          values?.moreAboutYou?.aboutYouWorkHistory?.trim() || "",
        hasUnspentConvictionsOrPendingCases:
          !!values?.moreAboutYou?.hasConvictionsOrPending,
        criminalConvictionsDetails:
          values?.moreAboutYou?.criminalConvictionsDetails?.trim() || "",
      })
    );
    fd.append("workPreferences", JSON.stringify(workPreferences));

    // Build complianceSkills from certificateEntries (dropdown + one file per row)
    const dbsCerts = [];
    const otherCerts = [];
    const filesByFieldKey = {};

    (values?.certificateEntries || []).forEach((entry) => {
      if (!entry?.certSlug) return;
      const cert = certsData.find((c) => c.slug === entry.certSlug);
      if (!cert) return;
      const hasFile = entry?.file instanceof File;
      const hasServiceNumber = !!entry?.serviceNumber?.trim?.();
      if (!hasFile && !hasServiceNumber) return;

      const fieldKey = cert.titleToUse;
      if (cert.hasServiceNumber) {
        const dbsEntry = { certSlug: cert.slug };
        if (hasServiceNumber) {
          dbsEntry.serviceNumber = String(entry.serviceNumber).trim();
        }
        dbsCerts.push(dbsEntry);
        if (hasFile) {
          if (!filesByFieldKey[fieldKey]) filesByFieldKey[fieldKey] = [];
          filesByFieldKey[fieldKey].push(entry.file);
        }
      } else {
        if (hasFile) {
          otherCerts.push({ certSlug: cert.slug });
          if (!filesByFieldKey[fieldKey]) filesByFieldKey[fieldKey] = [];
          filesByFieldKey[fieldKey].push(entry.file);
        }
      }
    });

    const complianceSkillsData = {
      hasInformationTrue: !!values?.complianceSkills?.hasInformationTrue,
    };
    if (dbsCerts.length > 0) complianceSkillsData.dbs = dbsCerts;
    if (otherCerts.length > 0) complianceSkillsData.otherCerts = otherCerts;
    fd.append("complianceSkills", JSON.stringify(complianceSkillsData));

    // 3) files (repeat the SAME key, no "[]")
    const appendOne = (key, f) => {
      if (!f) return;
      // Support File, Blob, FileList, or objects with .file
      if (f instanceof File || f instanceof Blob) {
        fd.append(key, f, (f && f.name) || undefined);
        return;
      }
      if (typeof FileList !== "undefined" && f instanceof FileList) {
        Array.from(f).forEach((ff) => fd.append(key, ff, ff.name));
        return;
      }
      if (Array.isArray(f)) {
        f.forEach((ff) => appendOne(key, ff));
        return;
      }
      if (f.file) {
        appendOne(key, f.file);
        return;
      }
    };

    const appendMany = (key, arr) => {
      (arr || []).forEach((item) => appendOne(key, item));
    };

    // RIGHT TO WORK
    appendMany("uploadId", values?.rightToWork?.uploadId); // multi
    appendOne("uploadSelfie", values?.personalInformation?.jobarkIdSelfie); // single - moved from RTW to PI in UI but payload stays same
    appendMany("proofOfAddress", values?.rightToWork?.proofOfAddress); // multi
    appendOne("drivingLicense", values?.rightToWork?.drivingLicense);
    appendOne("drivingLicenseBack", values?.rightToWork?.drivingLicenseBack);
    appendOne("onscreeningVideo", values?.onScreeningVideo);
    // appendOne("kycReport", values?.kycReport);

    // EMPLOYMENT TAX
    appendMany(
      "p45File",
      Array.isArray(values?.employmentTax?.p45Upload)
        ? values.employmentTax.p45Upload
        : values?.employmentTax?.p45Upload
        ? [values.employmentTax.p45Upload]
        : []
    );

    // if (
    //   values?.employmentTax?.hasP45 === "no" &&
    //   values?.employmentTax?.starterChecklistPdf instanceof File
    // ) {
    //   appendOne(
    //     "starterChecklistFiles",
    //     values.employmentTax.starterChecklistPdf
    //   );
    // }

    // COMPLIANCE: append files from certificateEntries (grouped by fieldKey)
    Object.keys(filesByFieldKey || {}).forEach((fieldKey) => {
      appendMany(fieldKey, filesByFieldKey[fieldKey]);
    });

    return fd;
  };

  // ===== Dynamic Sectors UI helpers =====
  const addSector = () => {
    const list = formik.values.workPreferences.sectors || [];
    setNested("workPreferences.sectors", [
      ...list,
      { sectorSlug: "", roles: [] },
    ]);
  };

  const removeSector = (idx) => {
    if (idx === 0) return; // first block required
    const list = [...(formik.values.workPreferences.sectors || [])];
    list.splice(idx, 1);
    setNested(
      "workPreferences.sectors",
      list.length ? list : [{ sectorSlug: "", roles: [] }]
    );
  };

  // NEW: helper to compute sector options for a specific row,
  // disabling any sector already chosen in other rows
  const sectorOptions = useMemo(
    () =>
      sectorsData.map((s) => ({
        value: s.slug,
        label: s.title,
        roles: s.roles || [],
      })),
    [sectorsData]
  );

  const getSectorOptionsForRow = (rowIndex) => {
    const selectedElsewhere = new Set(
      (formik.values.workPreferences.sectors || [])
        .map((s, i) => (i === rowIndex ? null : s.sectorSlug))
        .filter(Boolean)
    );

    // Keep every option, but mark as disabled if used elsewhere
    return sectorOptions.map((opt) => ({
      ...opt,
      disabled: selectedElsewhere.has(opt.value),
    }));
  };

  const setSectorSlug = (idx, slug) => {
    // Guard: prevent duplicates even if a user tries to force it
    const selectedElsewhere = new Set(
      (formik.values.workPreferences.sectors || [])
        .map((s, i) => (i === idx ? null : s.sectorSlug))
        .filter(Boolean)
    );
    if (slug && selectedElsewhere.has(slug)) {
      toastr.warning(t("sectorAlreadySelected"));
      return;
    }

    const list = [...(formik.values.workPreferences.sectors || [])];
    list[idx] = { sectorSlug: slug, roles: [] }; // reset roles when sector changes
    setNested("workPreferences.sectors", list);
    touchNested("workPreferences.sectors");
  };

  // Multi-select roles
  const setRolesForSector = (idx, roleSlugs) => {
    const list = [...(formik.values.workPreferences.sectors || [])];
    list[idx] = { ...list[idx], roles: roleSlugs };
    setNested("workPreferences.sectors", list);
    touchNested("workPreferences.sectors");
  };

  const getRoleOptions = (sectorSlug) => {
    const sec = sectorsData.find((s) => s.slug === sectorSlug);
    return (sec?.roles || []).map((r) => ({ value: r.slug, label: r.title }));
  };

  const toggleInArray = (path, val) => {
    const arr = new Set(formik.getFieldProps(path).value || []);
    if (arr.has(val)) arr.delete(val);
    else arr.add(val);
    setNested(path, Array.from(arr));
  };

  return (
    <>
      <CustomHeader />
      <div className="newsletter-page worker-onboarding-page mt-5 page-container">
        <div className="account-pages my-3 ">
          <Container>
            <Row className="justify-content-center">
              <Col md={12} lg={10} xl={9}>
               
                <div>
                  <div className="p-4 text-center">
                    <h2 className="text-primary mb-2 header-title">
                      {t("header")}
                    </h2>
                    <p className="text-primary mb-0 header-subtitle">
                      {t("workerOnBoardingSubtitle")}
                    </p>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={12} lg={10} xl={9}>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    console.log("[WorkerOnBoarding] Form submit clicked");
                    formik.validateForm().then((errors) => {
                      const hasErrors = Object.keys(errors).length > 0;
                      console.log(
                        "[WorkerOnBoarding] Validation result:",
                        hasErrors ? "INVALID" : "VALID"
                      );
                      if (hasErrors) {
                        console.log(
                          "[WorkerOnBoarding] Validation errors (these prevent onSubmit from running):",
                          errors
                        );
                        // Show which section has the first error (nested paths like personalInformation.firstName)
                        const firstPath = Object.keys(errors)[0];
                        console.log(
                          "[WorkerOnBoarding] First invalid field path:",
                          firstPath
                        );
                      }
                    });
                    formik.handleSubmit();
                    return false;
                  }}
                >
                  {workerOnBoardingMutation.isSuccess && (
                    <Alert color="success">{t("success")}</Alert>
                  )}
                  {workerOnBoardingMutation.isError && (
                    <Alert color="danger">{t("tryAgain")}</Alert>
                  )}

                  {/* ===== Personal Information ===== */}
                  <Card className="mb-3 form-card">
                    <CardHeader className="card-header-custom">
                      <h5 className="mb-0 card-header-title d-flex align-items-center gap-2 flex-wrap">
                        {t("personalInformation")}
                        <i
                          style={{ cursor: "help" }}
                          id="wo-h-pi-section"
                          className="bx bx-info-circle"
                        ></i>
                        <UncontrolledTooltip target="wo-h-pi-section">
                          {t("woTooltip_personal_section")}
                        </UncontrolledTooltip>
                      </h5>
                    </CardHeader>
                    <CardBody className="card-body-custom">
                      <Row>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("firstName")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="personalInformation.firstName"
                            value={formik.values.personalInformation.firstName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.personalInformation?.firstName &&
                              !!formik.errors.personalInformation?.firstName
                            }
                            placeholder={t("placeholderFirstName")}
                          />
                          <FormFeedback>
                            {formik.errors.personalInformation?.firstName}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("lastName")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="personalInformation.lastName"
                            value={formik.values.personalInformation.lastName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.personalInformation?.lastName &&
                              !!formik.errors.personalInformation?.lastName
                            }
                            placeholder={t("placeholderLastName")}
                          />
                          <FormFeedback>
                            {formik.errors.personalInformation?.lastName}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("dob")} <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="date"
                            name="personalInformation.dob"
                            max={new Date().toISOString().split("T")[0]}
                            value={formik.values.personalInformation.dob}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.personalInformation?.dob &&
                              !!formik.errors.personalInformation?.dob
                            }
                          />
                          <FormFeedback>
                            {formik.errors.personalInformation?.dob}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard d-flex align-items-center gap-1 flex-wrap">
                            {t("gender")} <span className="text-danger">*</span>
                            <i
                              style={{ cursor: "help" }}
                              id="wo-h-pi-gender"
                              className="bx bx-info-circle"
                            ></i>
                            <UncontrolledTooltip target="wo-h-pi-gender">
                              {t("woTooltip_personal_gender")}
                            </UncontrolledTooltip>
                          </Label>
                          <Input
                            type="select"
                            name="personalInformation.gender"
                            value={formik.values.personalInformation.gender}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.personalInformation?.gender &&
                              !!formik.errors.personalInformation?.gender
                            }
                          >
                            <option value="">{t("select")}</option>
                            <option value={GENDER_OPTIONS.MALE}>
                              {t("male")}
                            </option>
                            <option value={GENDER_OPTIONS.FEMALE}>
                              {t("female")}
                            </option>
                            <option value={GENDER_OPTIONS.OTHER}>
                              {t("other")}
                            </option>
                          </Input>
                          <FormFeedback>
                            {formik.errors.personalInformation?.gender}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("phoneNumber")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <FormPhoneInput
                            name="personalInformation.phoneNumber"
                            value={
                              formik.values.personalInformation.phoneNumber
                            }
                            touched={
                              formik.touched.personalInformation?.phoneNumber
                            }
                            error={
                              formik.errors.personalInformation?.phoneNumber
                            }
                            onBlur={() =>
                              touchNested("personalInformation.phoneNumber")
                            }
                            onChange={(v) =>
                              setNested("personalInformation.phoneNumber", v)
                            }
                          />
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard d-flex align-items-center gap-1 flex-wrap">
                            {t("email")} <span className="text-danger">*</span>
                            <i
                              style={{ cursor: "help" }}
                              id="wo-h-pi-email"
                              className="bx bx-info-circle"
                            ></i>
                            <UncontrolledTooltip target="wo-h-pi-email">
                              {t("woTooltip_personal_email")}
                            </UncontrolledTooltip>
                          </Label>
                          <Input
                            type="email"
                            name="personalInformation.email"
                            value={formik.values.personalInformation.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.personalInformation?.email &&
                              !!formik.errors.personalInformation?.email
                            }
                            placeholder={t("placeholderEmail")}
                          />
                          <FormFeedback>
                            {formik.errors.personalInformation?.email}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard d-flex align-items-center gap-1 flex-wrap">
                            {t("nationality")}{" "}
                            <span className="text-danger">*</span>
                            <i
                              style={{ cursor: "help" }}
                              id="wo-h-pi-nationality"
                              className="bx bx-info-circle"
                            ></i>
                            <UncontrolledTooltip target="wo-h-pi-nationality">
                              {t("woTooltip_personal_nationality")}
                            </UncontrolledTooltip>
                          </Label>
                          <Select
                            name="personalInformation.nationality"
                            options={nationalityOptions}
                            value={nationalityOptions.find(
                              (option) =>
                                option.value ===
                                formik.values.personalInformation.nationality
                            )}
                            onChange={(selectedOption) => {
                              setNested(
                                "personalInformation.nationality",
                                selectedOption ? selectedOption.value : ""
                              );
                            }}
                            onBlur={() =>
                              touchNested("personalInformation.nationality")
                            }
                            placeholder={t(
                              "selectOrTypeToSearch",
                              "Select or type to search..."
                            )}
                            isSearchable
                            isClearable
                            classNamePrefix="rs"
                            menuPortalTarget={
                              typeof document !== "undefined"
                                ? document.body
                                : null
                            }
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: "#fff",
                                zIndex: 9999,
                              }),
                              control: (base, state) => ({
                                ...base,
                                minHeight: 38,
                                backgroundColor: "#fff",
                                borderColor:
                                  formik.touched.personalInformation
                                    ?.nationality &&
                                  formik.errors.personalInformation?.nationality
                                    ? "#dc3545"
                                    : state.isFocused
                                    ? "#80bdff"
                                    : base.borderColor,
                                boxShadow:
                                  formik.touched.personalInformation
                                    ?.nationality &&
                                  formik.errors.personalInformation?.nationality
                                    ? "0 0 0 0.2rem rgba(220,53,69,.25)"
                                    : state.isFocused
                                    ? "0 0 0 0.2rem rgba(0,123,255,.25)"
                                    : base.boxShadow,
                                "&:hover": {
                                  borderColor:
                                    formik.touched.personalInformation
                                      ?.nationality &&
                                    formik.errors.personalInformation
                                      ?.nationality
                                      ? "#dc3545"
                                      : "#80bdff",
                                },
                              }),
                            }}
                          />
                          {formik.touched.personalInformation?.nationality &&
                            formik.errors.personalInformation?.nationality && (
                              <div className="invalid-feedback d-block">
                                {formik.errors.personalInformation?.nationality}
                              </div>
                            )}
                        </Col>

                        <Col md={12} className="mb-3">
                          <Label className="form-label-standard">
                            {t("languagesSpoken")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <CreatableSelect
                            isMulti
                            isSearchable
                            isClearable
                            options={getLanguageOptions()}
                            value={(
                              formik.values.personalInformation
                                .languagesSpoken || []
                            ).map((lang) => ({ value: lang, label: lang }))}
                            onChange={(selectedOptions) => {
                              const next = selectedOptions
                                ? selectedOptions.map((o) =>
                                    String(o.value).trim()
                                  )
                                : [];
                              setNested(
                                "personalInformation.languagesSpoken",
                                next.filter(Boolean)
                              );
                            }}
                            onBlur={() =>
                              touchNested("personalInformation.languagesSpoken")
                            }
                            formatCreateLabel={(inputValue) =>
                              `${t("addAnother")}: "${inputValue}"`
                            }
                            classNamePrefix="rs"
                            menuPortalTarget={
                              typeof document !== "undefined"
                                ? document.body
                                : null
                            }
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: "#fff",
                                zIndex: 9999,
                              }),
                              control: (base) => ({
                                ...base,
                                minHeight: 44,
                                backgroundColor: "#fff",
                                borderColor:
                                  formik.touched.personalInformation
                                    ?.languagesSpoken &&
                                  formik.errors.personalInformation
                                    ?.languagesSpoken
                                    ? "#dc3545"
                                    : base.borderColor,
                              }),
                            }}
                            placeholder={t("selectLanguages")}
                          />
                          {formik.touched.personalInformation
                            ?.languagesSpoken &&
                            formik.errors.personalInformation
                              ?.languagesSpoken && (
                              <div className="invalid-feedback d-block">
                                {
                                  formik.errors.personalInformation
                                    ?.languagesSpoken
                                }
                              </div>
                            )}
                        </Col>

                        <Col md={12} className="mb-3">
                          <Label className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <span className="d-flex align-items-center gap-1 flex-wrap">
                              {t("addressSearchLabel")}{" "}
                              <span className="text-danger">*</span>
                              <i
                                style={{ cursor: "help" }}
                                id="wo-h-pi-location"
                                className="bx bx-info-circle"
                              ></i>
                              <UncontrolledTooltip target="wo-h-pi-location">
                                {t("woTooltip_personal_location")}
                              </UncontrolledTooltip>
                            </span>
                            <Button
                              type="button"
                              color="secondary"
                              size="sm"
                              onClick={requestLocation}
                              disabled={geoLoading}
                            >
                              {geoLoading && (
                                <span className="spinner-border spinner-border-sm me-2" />
                              )}
                              {t("useMyLocation")}
                            </Button>
                          </Label>
                          {mapsLoaded ? (
                            <Autocomplete
                              options={UK_ADDRESS_AUTOCOMPLETE_OPTIONS}
                              onLoad={onLoadAddressAutocomplete}
                              onPlaceChanged={onAddressPlaceChanged}
                            >
                              <Input
                                name="personalInformation.addressSearch"
                                value={
                                  formik.values.personalInformation
                                    .addressSearch || ""
                                }
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder={t("addressSearchPlaceholder")}
                              />
                            </Autocomplete>
                          ) : (
                            <Input
                              disabled
                              placeholder={t("loadingMapsPlaceholder")}
                            />
                          )}
                          <small className="text-muted d-block mt-1">
                            {t("addressSearchHelp")}
                          </small>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("addressLine1")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="personalInformation.addressLine1"
                            value={
                              formik.values.personalInformation.addressLine1 ||
                              ""
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.personalInformation
                                ?.addressLine1 &&
                              !!formik.errors.personalInformation?.addressLine1
                            }
                          />
                          <FormFeedback>
                            {formik.errors.personalInformation?.addressLine1}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("addressLine2")}
                          </Label>
                          <Input
                            name="personalInformation.addressLine2"
                            value={
                              formik.values.personalInformation.addressLine2 ||
                              ""
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("postcode")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="personalInformation.postcode"
                            value={
                              formik.values.personalInformation.postcode || ""
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.personalInformation?.postcode &&
                              !!formik.errors.personalInformation?.postcode
                            }
                          />
                          <FormFeedback>
                            {formik.errors.personalInformation?.postcode}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("city")} <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="personalInformation.city"
                            value={formik.values.personalInformation.city || ""}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.personalInformation?.city &&
                              !!formik.errors.personalInformation?.city
                            }
                          />
                          <FormFeedback>
                            {formik.errors.personalInformation?.city}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("county")}
                          </Label>
                          <Input
                            name="personalInformation.county"
                            value={
                              formik.values.personalInformation.county || ""
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("country")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Select
                            options={addressCountryOptions}
                            value={addressCountryOptions.find(
                              (option) =>
                                option.value ===
                                formik.values.personalInformation.country
                            )}
                            onChange={() =>
                              setNested(
                                "personalInformation.country",
                                UK_COUNTRY
                              )
                            }
                            onBlur={() =>
                              touchNested("personalInformation.country")
                            }
                            placeholder={t("placeholderCountry")}
                            isSearchable={false}
                            isClearable={false}
                            isDisabled
                            classNamePrefix="rs"
                            menuPortalTarget={
                              typeof document !== "undefined"
                                ? document.body
                                : null
                            }
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                              control: (base, state) => ({
                                ...base,
                                minHeight: 38,
                                borderColor:
                                  formik.touched.personalInformation?.country &&
                                  formik.errors.personalInformation?.country
                                    ? "#dc3545"
                                    : state.isFocused
                                    ? "#80bdff"
                                    : base.borderColor,
                              }),
                            }}
                          />
                          {formik.touched.personalInformation?.country &&
                            formik.errors.personalInformation?.country && (
                              <div className="invalid-feedback d-block">
                                {formik.errors.personalInformation?.country}
                              </div>
                            )}
                        </Col>

                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("niNumber")}{" "}
                            <span className="text-muted small">
                              ({t("optional")})
                            </span>
                          </Label>
                          <Input
                            name="personalInformation.nationalInsuranceNumber"
                            value={
                              formik.values.personalInformation
                                .nationalInsuranceNumber
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.personalInformation
                                ?.nationalInsuranceNumber &&
                              !!formik.errors.personalInformation
                                ?.nationalInsuranceNumber
                            }
                            placeholder={t("placeholderNiNumber")}
                          />
                          <FormFeedback>
                            {
                              formik.errors.personalInformation
                                ?.nationalInsuranceNumber
                            }
                          </FormFeedback>
                        </Col>

                        {/* <Col md={12} className="mb-1 d-none">
                          <Input
                            type="hidden"
                            name="personalInformation.baseLat"
                            value={
                              formik.values.personalInformation.baseLat || ""
                            }
                          />
                          <Input
                            type="hidden"
                            name="personalInformation.baseLng"
                            value={
                              formik.values.personalInformation.baseLng || ""
                            }
                          />
                        </Col>
                        {(formik.touched.personalInformation?.baseLat ||
                          formik.touched.personalInformation?.baseLng) &&
                          (formik.errors.personalInformation?.baseLat ||
                            formik.errors.personalInformation?.baseLng) && (
                            <Col md={12} className="mb-3">
                              <div className="invalid-feedback d-block">
                                {formik.errors.personalInformation?.baseLat ||
                                  formik.errors.personalInformation?.baseLng ||
                                  t("pleaseCaptureLocation")}
                              </div>
                            </Col>
                          )} */}

                        {/* ===== JobArk ID Selfie ===== */}
                        <Col md={12} className="mb-3">
                          <Label className="d-flex align-items-center gap-1 flex-wrap">
                            {t("uploadSelfie")}{" "}
                            {t("jobarkIdParen", "(JobArk ID)")}{" "}
                            <span className="text-danger">*</span>
                            <i
                              style={{ cursor: "help" }}
                              id="wo-h-pi-selfie"
                              className="bx bx-info-circle"
                            ></i>
                            <UncontrolledTooltip target="wo-h-pi-selfie">
                              {t("woTooltip_personal_selfie")}
                            </UncontrolledTooltip>
                          </Label>
                          {formik.values.personalInformation?.jobarkIdSelfie ? (
                            /* Show captured/uploaded photo info with options to change */
                            <div className="mb-2">
                              <div className="p-2 bg-light rounded border border-success">
                                <div className="d-flex align-items-center justify-content-between text-success">
                                  <div className="d-flex align-items-center">
                                    <i className="bx bx-check-circle me-2 fs-5"></i>
                                    <div>
                                      <strong>{t("photoCaptured")}</strong>
                                      <div className="small text-muted">
                                        {formik.values.personalInformation
                                          .jobarkIdSelfie.name ||
                                          `${t("fileSize")}: ${(
                                            formik.values.personalInformation
                                              .jobarkIdSelfie.size /
                                            1024 /
                                            1024
                                          ).toFixed(2)} MB`}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      type="button"
                                      color="light"
                                      size="sm"
                                      onClick={() => {
                                        setNested(
                                          "personalInformation.jobarkIdSelfie",
                                          null
                                        );
                                        touchNested(
                                          "personalInformation.jobarkIdSelfie"
                                        );
                                      }}
                                      title={t("removePhoto")}
                                    >
                                      <i className="bx bx-x"></i>
                                    </Button>
                                    <Button
                                      type="button"
                                      color="primary"
                                      size="sm"
                                      outline
                                      onClick={openWebcam}
                                      title={t("retakePhoto")}
                                    >
                                      <i className="bx bx-camera me-1"></i>
                                      {t("retake")}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Show webcam and upload buttons */
                            <div className="mb-2">
                              <div className="d-flex gap-2 mb-2 flex-wrap">
                                <Button
                                  type="button"
                                  color="primary"
                                  className="flex-fill"
                                  onClick={openWebcam}
                                  title={t("openWebcam")}
                                >
                                  <i className="bx bx-camera me-2"></i>
                                  {t("takeLiveSelfie")}
                                </Button>
                                <label
                                  htmlFor="selfieFileUpload"
                                  className="btn btn-outline-secondary flex-fill"
                                  style={{ cursor: "pointer", margin: 0 }}
                                  title={t("uploadFile")}
                                >
                                  <i className="bx bx-upload me-2"></i>
                                  {t("uploadFile")}
                                </label>
                                <Input
                                  id="selfieFileUpload"
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      if (
                                        !isUnder(
                                          file,
                                          FILE_VALIDATION.SELFIE_MAX_MB
                                        )
                                      ) {
                                        toastr.error(
                                          t("fileTooLarge", {
                                            n: FILE_VALIDATION.SELFIE_MAX_MB,
                                          })
                                        );
                                        e.target.value = "";
                                        return;
                                      }
                                      if (
                                        !FILE_VALIDATION.ALLOWED_IMAGE_EXTENSIONS.test(
                                          file.name
                                        )
                                      ) {
                                        toastr.error(t("fileTypeNotAllowed"));
                                        e.target.value = "";
                                        return;
                                      }
                                      handleFileChange(
                                        "personalInformation.jobarkIdSelfie",
                                        file,
                                        FILE_VALIDATION.SELFIE_MAX_MB
                                      );
                                      touchNested(
                                        "personalInformation.jobarkIdSelfie"
                                      );
                                      formik.setFieldError(
                                        "personalInformation.jobarkIdSelfie",
                                        undefined
                                      );
                                    }
                                    e.target.value = "";
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          {/* Only show error if field is touched AND no file is present */}
                          {formik.touched.personalInformation?.jobarkIdSelfie &&
                            formik.errors.personalInformation?.jobarkIdSelfie &&
                            !formik.values.personalInformation
                              ?.jobarkIdSelfie && (
                              <div className="invalid-feedback d-block">
                                {
                                  formik.errors.personalInformation
                                    ?.jobarkIdSelfie
                                }
                              </div>
                            )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ===== Emergency Contact ===== */}
                  <Card className="mb-3 form-card">
                    <CardHeader className="card-header-custom">
                      <h5 className="mb-0 card-header-title d-flex align-items-center gap-2 flex-wrap">
                        {t("emergencyContact")}
                        <i
                          style={{ cursor: "help" }}
                          id="wo-h-ec-section"
                          className="bx bx-info-circle"
                        ></i>
                        <UncontrolledTooltip target="wo-h-ec-section">
                          {t("woTooltip_emergency_section")}
                        </UncontrolledTooltip>
                      </h5>
                    </CardHeader>
                    <CardBody className="card-body-custom">
                      <Row>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("firstName")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="emergencyContact.firstName"
                            value={formik.values.emergencyContact.firstName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.emergencyContact?.firstName &&
                              !!formik.errors.emergencyContact?.firstName
                            }
                            placeholder={t("placeholderFirstName")}
                          />
                          <FormFeedback>
                            {formik.errors.emergencyContact?.firstName}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("lastName")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="emergencyContact.lastName"
                            value={formik.values.emergencyContact.lastName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.emergencyContact?.lastName &&
                              !!formik.errors.emergencyContact?.lastName
                            }
                            placeholder={t("placeholderLastName")}
                          />
                          <FormFeedback>
                            {formik.errors.emergencyContact?.lastName}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("relationship")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            name="emergencyContact.relationshipToWorker"
                            value={
                              formik.values.emergencyContact
                                .relationshipToWorker
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.emergencyContact
                                ?.relationshipToWorker &&
                              !!formik.errors.emergencyContact
                                ?.relationshipToWorker
                            }
                          >
                            <option value="">{t("select")}</option>
                            {EMERGENCY_RELATIONSHIP_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {t(opt.labelKey)}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>
                            {
                              formik.errors.emergencyContact
                                ?.relationshipToWorker
                            }
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("phoneNumber")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <FormPhoneInput
                            name="emergencyContact.phoneNumber"
                            value={formik.values.emergencyContact.phoneNumber}
                            touched={
                              formik.touched.emergencyContact?.phoneNumber
                            }
                            error={formik.errors.emergencyContact?.phoneNumber}
                            onBlur={() =>
                              touchNested("emergencyContact.phoneNumber")
                            }
                            onChange={(v) =>
                              setNested("emergencyContact.phoneNumber", v)
                            }
                          />
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("altPhone")}{" "}
                            <span className="text-muted small">
                              ({t("optional")})
                            </span>
                          </Label>
                          <FormPhoneInput
                            name="emergencyContact.alternativePhoneNumber"
                            value={
                              formik.values.emergencyContact
                                .alternativePhoneNumber
                            }
                            touched={
                              formik.touched.emergencyContact
                                ?.alternativePhoneNumber
                            }
                            error={
                              formik.errors.emergencyContact
                                ?.alternativePhoneNumber
                            }
                            onBlur={() =>
                              touchNested(
                                "emergencyContact.alternativePhoneNumber"
                              )
                            }
                            onChange={(v) =>
                              setNested(
                                "emergencyContact.alternativePhoneNumber",
                                v
                              )
                            }
                          />
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ===== Right to Work ===== */}
                  <Card className="mb-3 form-card">
                    <CardHeader className="card-header-custom">
                      <h5 className="mb-0 card-header-title d-flex align-items-center gap-2 flex-wrap">
                        {t("rightToWork")}
                        <i
                          style={{ cursor: "help" }}
                          id="wo-h-rtw-section"
                          className="bx bx-info-circle"
                        ></i>
                        <UncontrolledTooltip target="wo-h-rtw-section">
                          {t("woTooltip_rtw_section")}
                        </UncontrolledTooltip>
                      </h5>
                    </CardHeader>
                    <CardBody className="card-body-custom">
                      <Row>
                        <Col md={12} className="mb-3">
                          <Label className="form-label-standard">
                            {t("rtwLegalRightToWorkUkQuestion")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            name="rightToWork.holdsLegalRightToWorkUk"
                            value={
                              formik.values.rightToWork.holdsLegalRightToWorkUk
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "no") {
                                setNested(
                                  "rightToWork.holdsLegalRightToWorkUk",
                                  "no"
                                );
                                setNested("rightToWork.rightToWorkStatus", "");
                                setNested("rightToWork.shareCode", "");
                                setNested("rightToWork.visaExpiryDate", "");
                                setNested("rightToWork.drivingLicense", null);
                                setNested(
                                  "rightToWork.drivingLicenseBack",
                                  null
                                );
                                setNested("rightToWork.dvlaShareCode", "");
                                setNested(
                                  "rightToWork.drivingLicenseExpiryDate",
                                  ""
                                );
                                setNested("rightToWork.licenseNumber", "");
                                setNested("rightToWork.uploadId", []);
                                setNested("rightToWork.proofOfAddress", []);
                                setRtwIneligibleModalOpen(true);
                                return;
                              }
                              if (v === "yes") {
                                setNested(
                                  "rightToWork.holdsLegalRightToWorkUk",
                                  "yes"
                                );
                                return;
                              }
                              setNested(
                                "rightToWork.holdsLegalRightToWorkUk",
                                ""
                              );
                              setNested("rightToWork.rightToWorkStatus", "");
                              setNested("rightToWork.shareCode", "");
                              setNested("rightToWork.visaExpiryDate", "");
                              setNested("rightToWork.drivingLicense", null);
                              setNested("rightToWork.drivingLicenseBack", null);
                              setNested("rightToWork.dvlaShareCode", "");
                              setNested(
                                "rightToWork.drivingLicenseExpiryDate",
                                ""
                              );
                              setNested("rightToWork.licenseNumber", "");
                            }}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!formik.touched.rightToWork
                                ?.holdsLegalRightToWorkUk &&
                              !!formik.errors.rightToWork
                                ?.holdsLegalRightToWorkUk
                            }
                          >
                            <option value="">{t("select")}</option>
                            <option value="yes">{t("yes")}</option>
                            <option value="no">{t("no")}</option>
                          </Input>
                          <FormFeedback>
                            {formik.errors.rightToWork?.holdsLegalRightToWorkUk}
                          </FormFeedback>
                        </Col>

                        {formik.values.rightToWork.holdsLegalRightToWorkUk ===
                          "yes" && (
                          <>
                            <Col md={12} className="mb-3">
                              <Label className="form-label-standard d-flex align-items-center gap-1 flex-wrap">
                                {t("rightToWorkStatus")}{" "}
                                <span className="text-danger">*</span>
                                {formik.values.rightToWork.rightToWorkStatus ===
                                  RIGHT_TO_WORK_STATUS_STUDENT_VISA && (
                                  <>
                                    <span className="text-muted">
                                      {t(
                                        "workerOnBoarding_visaStatus_studentVisa"
                                      )}
                                    </span>
                                    <i
                                      style={{ cursor: "help" }}
                                      id="wo-h-rtw-student-visa"
                                      className="bx bx-info-circle"
                                    ></i>
                                    <UncontrolledTooltip
                                      target="wo-h-rtw-student-visa"
                                      placement="top"
                                    >
                                      {t("woTooltip_rtw_studentVisa")}
                                    </UncontrolledTooltip>
                                  </>
                                )}
                              </Label>
                              <Input
                                type="select"
                                name="rightToWork.rightToWorkStatus"
                                value={
                                  formik.values.rightToWork.rightToWorkStatus
                                }
                                onChange={(e) => {
                                  const next = e.target.value;
                                  const prev =
                                    formik.values.rightToWork.rightToWorkStatus;
                                  formik.setFieldValue(
                                    "rightToWork.rightToWorkStatus",
                                    next
                                  );
                                  const wasBi =
                                    prev === RIGHT_TO_WORK_STATUS_BRITISH_IRISH;
                                  const nowBi =
                                    next === RIGHT_TO_WORK_STATUS_BRITISH_IRISH;
                                  if (wasBi !== nowBi) {
                                    setNested("rightToWork.shareCode", "");
                                    setNested("rightToWork.visaExpiryDate", "");
                                  }
                                }}
                                onBlur={formik.handleBlur}
                                invalid={
                                  formik.touched.rightToWork
                                    ?.rightToWorkStatus &&
                                  !!formik.errors.rightToWork?.rightToWorkStatus
                                }
                              >
                                <option value="">{t("select")}</option>
                                {RIGHT_TO_WORK_STATUS_OPTIONS_ALL.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </Input>
                              <FormFeedback>
                                {formik.errors.rightToWork?.rightToWorkStatus}
                              </FormFeedback>
                            </Col>

                            {!!formik.values.rightToWork.rightToWorkStatus && (
                              <>
                                <Col md={6} className="mb-3">
                                  <Label>
                                    {t("photoIdUpload")}
                                    <span className="text-danger"> *</span>
                                  </Label>
                                  <small className="text-muted d-block mb-1">
                                    {t("photoIdUploadHint")}
                                  </small>
                                  <Input
                                    type="file"
                                    accept=".pdf,image/*"
                                    multiple
                                    onChange={(e) =>
                                      handleFilesChange(
                                        "rightToWork.uploadId",
                                        e.currentTarget.files,
                                        e.currentTarget
                                      )
                                    }
                                    onBlur={() =>
                                      touchNested("rightToWork.uploadId")
                                    }
                                    invalid={
                                      formik.touched.rightToWork?.uploadId &&
                                      !!formik.errors.rightToWork?.uploadId
                                    }
                                  />
                                  {formik.touched.rightToWork?.uploadId &&
                                    formik.errors.rightToWork?.uploadId && (
                                      <div className="invalid-feedback d-block">
                                        {typeof formik.errors.rightToWork
                                          ?.uploadId === "string"
                                          ? formik.errors.rightToWork.uploadId
                                          : t("invalidFiles")}
                                      </div>
                                    )}
                                </Col>
                                <Col md={6} className="mb-3">
                                  <Label className="d-flex align-items-center flex-wrap gap-1">
                                    <span>
                                      {t("proofOfAddress")}{" "}
                                      <span className="text-danger">*</span>
                                    </span>
                                    <i
                                      style={{ cursor: "help" }}
                                      id="wo-h-rtw-proof-address"
                                      className="bx bx-info-circle"
                                    ></i>
                                    <UncontrolledTooltip
                                      target="wo-h-rtw-proof-address"
                                      placement="top"
                                    >
                                      <ProofOfAddressAcceptableContent />
                                    </UncontrolledTooltip>
                                  </Label>
                                  <Input
                                    type="file"
                                    accept=".pdf,image/*"
                                    multiple
                                    onChange={(e) =>
                                      handleFilesChange(
                                        "rightToWork.proofOfAddress",
                                        e.currentTarget.files,
                                        e.currentTarget
                                      )
                                    }
                                    onBlur={() =>
                                      touchNested("rightToWork.proofOfAddress")
                                    }
                                    invalid={
                                      formik.touched.rightToWork
                                        ?.proofOfAddress &&
                                      !!formik.errors.rightToWork
                                        ?.proofOfAddress
                                    }
                                  />
                                  {formik.touched.rightToWork?.proofOfAddress &&
                                    formik.errors.rightToWork
                                      ?.proofOfAddress && (
                                      <div className="invalid-feedback d-block">
                                        {typeof formik.errors.rightToWork
                                          ?.proofOfAddress === "string"
                                          ? formik.errors.rightToWork
                                              .proofOfAddress
                                          : t("invalidFiles")}
                                      </div>
                                    )}
                                </Col>

                                {formik.values.rightToWork.rightToWorkStatus !==
                                  RIGHT_TO_WORK_STATUS_BRITISH_IRISH && (
                                  <>
                                    <Col md={6} className="mb-3">
                                      <Label>
                                        {t("homeOfficeShareCode")}{" "}
                                        <span className="text-danger">*</span>
                                      </Label>
                                      <Input
                                        name="rightToWork.shareCode"
                                        value={
                                          formik.values.rightToWork.shareCode
                                        }
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        invalid={
                                          formik.touched.rightToWork
                                            ?.shareCode &&
                                          !!formik.errors.rightToWork?.shareCode
                                        }
                                        placeholder={t("placeholderShareCode")}
                                      />
                                      <FormFeedback>
                                        {formik.errors.rightToWork?.shareCode}
                                      </FormFeedback>
                                      <small className="text-muted d-block mt-1">
                                        {t("homeOfficeShareCodeHelperPrefix")}{" "}
                                        <a
                                          href={UK_HOME_OFFICE_SHARE_CODE_URL}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {t("shareCodeHelperLinkText")}
                                        </a>
                                        .
                                      </small>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                      <Label>
                                        {t("visaExpiryDate")}{" "}
                                        <span className="text-danger">*</span>
                                      </Label>
                                      <Input
                                        type="date"
                                        name="rightToWork.visaExpiryDate"
                                        value={
                                          formik.values.rightToWork
                                            .visaExpiryDate
                                        }
                                        min={
                                          new Date().toISOString().split("T")[0]
                                        }
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        invalid={
                                          formik.touched.rightToWork
                                            ?.visaExpiryDate &&
                                          !!formik.errors.rightToWork
                                            ?.visaExpiryDate
                                        }
                                      />
                                      <FormFeedback>
                                        {
                                          formik.errors.rightToWork
                                            ?.visaExpiryDate
                                        }
                                      </FormFeedback>
                                    </Col>
                                  </>
                                )}
                              </>
                            )}
                          </>
                        )}

                        {/* Consent Checkbox */}
                        <Col md={12} className="mb-1">
                          <div className="form-check">
                            <Input
                              id="trustIdConsent"
                              className="form-check-input"
                              type="checkbox"
                              name="rightToWork.consentTrustID"
                              checked={formik.values.rightToWork.consentTrustID}
                              onChange={setChecked(
                                "rightToWork.consentTrustID"
                              )}
                              onBlur={() =>
                                touchNested("rightToWork.consentTrustID")
                              }
                              invalid={
                                formik.touched.rightToWork?.consentTrustID &&
                                !!formik.errors.rightToWork?.consentTrustID
                              }
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="trustIdConsent"
                            >
                              {t("consentTrustID")}{" "}
                              <span className="text-danger">*</span>
                            </Label>
                          </div>
                          {formik.touched.rightToWork?.consentTrustID &&
                            formik.errors.rightToWork?.consentTrustID && (
                              <div className="invalid-feedback d-block">
                                {formik.errors.rightToWork?.consentTrustID}
                              </div>
                            )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ===== KYC report ===== */}
                  {/* <Card className="mb-3 form-card">
                    <CardHeader className="card-header-custom">
                      <h5 className="mb-0 card-header-title">
                        {t("kycReport")}
                      </h5>
                    </CardHeader>
                    <CardBody className="card-body-custom">
                      <Row>
                        <Col md={12} className="mb-1">
                          <Label>{t("kycReportUpload")}</Label>
                          <Input
                            type="file"
                            accept=".pdf,image/*"
                            onChange={(e) =>
                              handleFileChange(
                                "kycReport",
                                e.currentTarget.files?.[0] || null,
                                FILE_VALIDATION.MAX_MB
                              )
                            }
                            onBlur={() => touchNested("kycReport")}
                            invalid={
                              !!formik.touched.kycReport &&
                              !!formik.errors.kycReport
                            }
                          />
                          {formik.touched.kycReport &&
                            formik.errors.kycReport && (
                              <div className="invalid-feedback d-block">
                                {formik.errors.kycReport}
                              </div>
                            )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card> */}

                  {/* ===== Employment Tax ===== */}
                  <Card className="mb-3 form-card">
                    <CardHeader className="card-header-custom">
                      <h5 className="mb-0 card-header-title d-flex align-items-center gap-2 flex-wrap">
                        {t("employmentTax")}
                        <i
                          style={{ cursor: "help" }}
                          id="wo-h-emptax-section"
                          className="bx bx-info-circle"
                        ></i>
                        <UncontrolledTooltip target="wo-h-emptax-section">
                          {t("woTooltip_empTax_section")}
                        </UncontrolledTooltip>
                      </h5>
                    </CardHeader>
                    <CardBody className="card-body-custom">
                      <Row>
                        {/* P45 Yes/No Dropdown */}
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard d-flex align-items-center gap-1 flex-wrap">
                            {t("hasP45")} <span className="text-danger">*</span>
                            <i
                              style={{ cursor: "help" }}
                              id="wo-h-emp-p45"
                              className="bx bx-info-circle"
                            ></i>
                            <UncontrolledTooltip target="wo-h-emp-p45">
                              {t("woTooltip_empTax_p45")}
                            </UncontrolledTooltip>
                          </Label>
                          <Input
                            type="select"
                            name="employmentTax.hasP45"
                            value={formik.values.employmentTax.hasP45}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setNested("employmentTax.hasP45", nextValue);
                              if (nextValue === "yes") {
                                setNested(
                                  "employmentTax.starterChecklistChoice",
                                  ""
                                );
                                setNested(
                                  "employmentTax.hasPostgraduateLoan",
                                  ""
                                );
                                setNested("employmentTax.planType", "");
                              }
                              if (nextValue === "no") {
                                setNested("employmentTax.p45Upload", null);
                              }
                            }}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.employmentTax?.hasP45 &&
                              !!formik.errors.employmentTax?.hasP45
                            }
                          >
                            <option value="">{t("select") || "Select"}</option>
                            <option value="yes">{t("yes") || "Yes"}</option>
                            <option value="no">{t("no") || "No"}</option>
                          </Input>
                          <FormFeedback>
                            {formik.errors.employmentTax?.hasP45}
                          </FormFeedback>
                        </Col>

                        {/* When P45 is "yes": Show P45 Upload, Notes, and Continue without link */}
                        {formik.values.employmentTax.hasP45 === "yes" && (
                          <>
                            <Col md={6} className="mb-3">
                              <Label>{t("p45Upload")}</Label>
                              <Input
                                type="file"
                                accept=".pdf,image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "employmentTax.p45Upload",
                                    e.currentTarget.files?.[0] || null,
                                    FILE_VALIDATION.MAX_MB
                                  )
                                }
                                onBlur={() =>
                                  touchNested("employmentTax.p45Upload")
                                }
                                invalid={
                                  formik.touched.employmentTax?.p45Upload &&
                                  !!formik.errors.employmentTax?.p45Upload
                                }
                              />
                              {formik.touched.employmentTax?.p45Upload &&
                                formik.errors.employmentTax?.p45Upload && (
                                  <div className="invalid-feedback d-block">
                                    {formik.errors.employmentTax?.p45Upload}
                                  </div>
                                )}
                            </Col>

                            <Col md={12} className="mb-3">
                              <button
                                type="button"
                                className="btn btn-link p-0 text-decoration-none"
                                onClick={() =>
                                  setNested("employmentTax.hasP45", "no")
                                }
                                style={{
                                  textAlign: "left",
                                  fontSize: "inherit",
                                }}
                              >
                                {t("cantUploadContinueWithoutP45")}
                              </button>
                              <div className="text-muted small mt-1">
                                {t("clickingThisUnselectsP45")}
                              </div>
                            </Col>
                          </>
                        )}

                        {formik.values.employmentTax.hasP45 === "no" && (
                          <>
                            {/* <Col md={12} className="mb-3">
                              <p className="text-muted mb-2">
                                {t("starterChecklistIntro")}
                              </p>
                              <a
                                href="https://www.gov.uk/guidance/starter-checklist-for-paye"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                https://www.gov.uk/guidance/starter-checklist-for-paye
                              </a>
                              <p className="mt-2 mb-2">
                                {t("starterChecklistUploadHint")}
                              </p>
                              <Label>{t("starterChecklistPdfLabel")}</Label>
                              <Input
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={(e) =>
                                  handleFileChange(
                                    "employmentTax.starterChecklistPdf",
                                    e.currentTarget.files?.[0] || null,
                                    FILE_VALIDATION.MAX_MB
                                  )
                                }
                                onBlur={() =>
                                  touchNested(
                                    "employmentTax.starterChecklistPdf"
                                  )
                                }
                                invalid={
                                  formik.touched.employmentTax
                                    ?.starterChecklistPdf &&
                                  !!formik.errors.employmentTax
                                    ?.starterChecklistPdf
                                }
                              />
                              {formik.touched.employmentTax
                                ?.starterChecklistPdf &&
                                formik.errors.employmentTax
                                  ?.starterChecklistPdf && (
                                  <div className="invalid-feedback d-block">
                                    {
                                      formik.errors.employmentTax
                                        ?.starterChecklistPdf
                                    }
                                  </div>
                                )}
                            </Col> */}
                            <Col md={12} className="mb-3">
                              <Label className="form-label-standard d-block">
                                Select which applies{" "}
                                <span className="text-danger">*</span>
                              </Label>
                              {STARTER_CHECKLIST_OPTIONS.map((option) => {
                                const id = `starter-checklist-${option.value}`;
                                return (
                                  <div
                                    className="form-check mb-2"
                                    key={option.value}
                                  >
                                    <Input
                                      id={id}
                                      className="form-check-input"
                                      type="radio"
                                      name="employmentTax.starterChecklistChoice"
                                      value={option.value}
                                      checked={
                                        formik.values.employmentTax
                                          .starterChecklistChoice ===
                                        option.value
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                    />
                                    <Label
                                      className="form-check-label"
                                      htmlFor={id}
                                    >
                                      <span className="d-block">
                                        {option.intro}
                                      </span>
                                      {option.bullets?.length > 0 && (
                                        <ul className="mb-0 ps-3">
                                          {option.bullets.map((item) => (
                                            <li key={item}>{item}</li>
                                          ))}
                                        </ul>
                                      )}
                                    </Label>
                                  </div>
                                );
                              })}
                              {formik.touched.employmentTax
                                ?.starterChecklistChoice &&
                                formik.errors.employmentTax
                                  ?.starterChecklistChoice && (
                                  <div className="invalid-feedback d-block">
                                    {
                                      formik.errors.employmentTax
                                        ?.starterChecklistChoice
                                    }
                                  </div>
                                )}
                            </Col>
                            <Col md={12} className="mb-3">
                              <Label className="form-label-standard d-block">
                                Do you have a student or postgraduate loan?{" "}
                                <span className="text-danger">*</span>
                              </Label>
                              <div className="d-flex gap-3 flex-wrap">
                                <div className="form-check">
                                  <Input
                                    id="student-loan-yes"
                                    className="form-check-input"
                                    type="radio"
                                    name="employmentTax.hasPostgraduateLoan"
                                    value="yes"
                                    checked={
                                      formik.values.employmentTax
                                        .hasPostgraduateLoan === "yes"
                                    }
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor="student-loan-yes"
                                  >
                                    {t("yes") || "Yes"}
                                  </Label>
                                </div>
                                <div className="form-check">
                                  <Input
                                    id="student-loan-no"
                                    className="form-check-input"
                                    type="radio"
                                    name="employmentTax.hasPostgraduateLoan"
                                    value="no"
                                    checked={
                                      formik.values.employmentTax
                                        .hasPostgraduateLoan === "no"
                                    }
                                    onChange={(e) => {
                                      formik.handleChange(e);
                                      setNested("employmentTax.planType", "");
                                    }}
                                    onBlur={formik.handleBlur}
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor="student-loan-no"
                                  >
                                    {t("no") || "No"}
                                  </Label>
                                </div>
                              </div>
                              {formik.touched.employmentTax
                                ?.hasPostgraduateLoan &&
                                formik.errors.employmentTax
                                  ?.hasPostgraduateLoan && (
                                  <div className="invalid-feedback d-block">
                                    {
                                      formik.errors.employmentTax
                                        ?.hasPostgraduateLoan
                                    }
                                  </div>
                                )}
                            </Col>
                            {formik.values.employmentTax.hasPostgraduateLoan ===
                              "yes" && (
                              <Col md={6} className="mb-3">
                                <Label className="form-label-standard">
                                  Select the correct student loan plan type or
                                  postgraduate loan{" "}
                                  <span className="text-danger">*</span>
                                </Label>
                                <Input
                                  type="select"
                                  name="employmentTax.planType"
                                  value={formik.values.employmentTax.planType}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  invalid={
                                    formik.touched.employmentTax?.planType &&
                                    !!formik.errors.employmentTax?.planType
                                  }
                                >
                                  <option value="">
                                    {t("select") || "Select"}
                                  </option>
                                  {STUDENT_LOAN_PLAN_OPTIONS.map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </Input>
                                <FormFeedback>
                                  {formik.errors.employmentTax?.planType}
                                </FormFeedback>
                              </Col>
                            )}
                            {/* <Col md={12} className="mb-3">
                              <Label>{t("notes")}</Label>
                              <Input
                                type="textarea"
                                name="employmentTax.notes"
                                value={formik.values.employmentTax.notes || ""}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                rows={2}
                              />
                            </Col> */}
                          </>
                        )}

                        {/* Accept Emergency Tax Code - always shown */}
                        <Col md={12} className="mb-3">
                          <div className="form-check">
                            <Input
                              id="acceptEmergency"
                              className="form-check-input"
                              type="checkbox"
                              name="employmentTax.acceptEmergencyTaxCode"
                              checked={
                                formik.values.employmentTax
                                  .acceptEmergencyTaxCode
                              }
                              onChange={setChecked(
                                "employmentTax.acceptEmergencyTaxCode"
                              )}
                              onBlur={() =>
                                touchNested(
                                  "employmentTax.acceptEmergencyTaxCode"
                                )
                              }
                              invalid={
                                formik.touched.employmentTax
                                  ?.acceptEmergencyTaxCode &&
                                !!formik.errors.employmentTax
                                  ?.acceptEmergencyTaxCode
                              }
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="acceptEmergency"
                            >
                              {t("acceptEmergencyTax")}{" "}
                              <span className="text-danger">*</span>
                            </Label>
                          </div>
                          {formik.touched.employmentTax
                            ?.acceptEmergencyTaxCode &&
                            formik.errors.employmentTax
                              ?.acceptEmergencyTaxCode && (
                              <div className="invalid-feedback d-block">
                                {
                                  formik.errors.employmentTax
                                    ?.acceptEmergencyTaxCode
                                }
                              </div>
                            )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ===== Bank Details ===== */}
                  <Card className="mb-3 form-card">
                    <CardHeader className="card-header-custom">
                      <h5 className="mb-0 card-header-title d-flex align-items-center gap-2 flex-wrap">
                        {t("bankDetails")}
                        <i
                          style={{ cursor: "help" }}
                          id="wo-h-bank-section"
                          className="bx bx-info-circle"
                        ></i>
                        <UncontrolledTooltip target="wo-h-bank-section">
                          {t("woTooltip_bank_section")}
                        </UncontrolledTooltip>
                      </h5>
                    </CardHeader>
                    <CardBody className="card-body-custom">
                      <Row>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("accHolder")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="bankDetails.accountHolderName"
                            value={formik.values.bankDetails.accountHolderName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.bankDetails?.accountHolderName &&
                              !!formik.errors.bankDetails?.accountHolderName
                            }
                            placeholder={t("placeholderAccHolder")}
                          />
                          <FormFeedback>
                            {formik.errors.bankDetails?.accountHolderName}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("sortCode")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="bankDetails.sortCode"
                            value={formik.values.bankDetails.sortCode}
                            onChange={handleSortCodeChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.bankDetails?.sortCode &&
                              !!formik.errors.bankDetails?.sortCode
                            }
                            placeholder={t(
                              "placeholderSortCode",
                              "00-00-00"
                            )}
                            maxLength="8"
                          />
                          <FormFeedback>
                            {formik.errors.bankDetails?.sortCode}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("accountNumber")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="bankDetails.accountNumber"
                            value={formik.values.bankDetails.accountNumber}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.bankDetails?.accountNumber &&
                              !!formik.errors.bankDetails?.accountNumber
                            }
                            placeholder={t(
                              "placeholderAccountNumber",
                              "12345678"
                            )}
                          />
                          <FormFeedback>
                            {formik.errors.bankDetails?.accountNumber}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("bankName")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="bankDetails.bankName"
                            value={formik.values.bankDetails.bankName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.bankDetails?.bankName &&
                              !!formik.errors.bankDetails?.bankName
                            }
                            placeholder={t("placeholderBankName")}
                          />
                          <FormFeedback>
                            {formik.errors.bankDetails?.bankName}
                          </FormFeedback>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ===== More about you ===== */}
                  <Card className="mb-3 form-card">
                    <CardHeader className="card-header-custom">
                      <h5 className="mb-0 card-header-title d-flex align-items-center gap-2 flex-wrap">
                        {t("moreAboutYouTitle")}
                        <i
                          style={{ cursor: "help" }}
                          id="wo-h-more-section"
                          className="bx bx-info-circle"
                        ></i>
                        <UncontrolledTooltip target="wo-h-more-section">
                          {t("woTooltip_more_section")}
                        </UncontrolledTooltip>
                      </h5>
                    </CardHeader>
                    <CardBody className="card-body-custom">
                      {/* Health, Accessibility, and Adjustments */}
                      <div className="mb-4">
                        <h6 className="text-dark mb-2">
                          {t("moreAboutYouHealthTitle")}
                        </h6>
                        <p className="text-muted small mb-2">
                          {t("moreAboutYouHealthIntro")}
                        </p>
                        <Input
                          type="textarea"
                          name="moreAboutYou.healthAccessibilityDetails"
                          value={
                            formik.values.moreAboutYou
                              .healthAccessibilityDetails
                          }
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          rows={4}
                          placeholder={t("moreAboutYouHealthPlaceholder")}
                          className="form-control"
                          invalid={moreAboutYouFieldShowError(
                            "healthAccessibilityDetails"
                          )}
                        />
                        {moreAboutYouFieldShowError(
                          "healthAccessibilityDetails"
                        ) && (
                          <div className="invalid-feedback d-block">
                            {
                              formik.errors.moreAboutYou
                                .healthAccessibilityDetails
                            }
                          </div>
                        )}
                      </div>

                      {/* Criminal Convictions and Pending Cases */}
                      <div className="mb-4">
                        <h6 className="text-dark mb-2 d-flex align-items-center gap-1 flex-wrap">
                          {t("moreAboutYouConvictionsTitle")}
                          <i
                            style={{ cursor: "help" }}
                            id="wo-h-more-conv"
                            className="bx bx-info-circle"
                          ></i>
                          <UncontrolledTooltip target="wo-h-more-conv">
                            {t("woTooltip_more_convictions")}
                          </UncontrolledTooltip>
                        </h6>
                        <p className="text-muted small mb-2">
                          {t("moreAboutYouConvictionsIntro")}
                        </p>
                        <Row>
                          <Col md={12} className="mb-2">
                            <div className="form-check">
                              <Input
                                id="hasNoConvictions"
                                className="form-check-input"
                                type="radio"
                                name="moreAboutYouCriminalConvictions"
                                checked={
                                  formik.values.moreAboutYou.hasNoConvictions
                                }
                                onChange={() => setConvictionsChoice("none")}
                                invalid={convictionsRadiosShowError}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="hasNoConvictions"
                              >
                                {t("moreAboutYouConvictionsNone")}
                              </Label>
                            </div>
                          </Col>
                          <Col md={12} className="mb-2">
                            <div className="form-check">
                              <Input
                                id="hasConvictionsOrPending"
                                className="form-check-input"
                                type="radio"
                                name="moreAboutYouCriminalConvictions"
                                checked={
                                  formik.values.moreAboutYou
                                    .hasConvictionsOrPending
                                }
                                onChange={() => setConvictionsChoice("has")}
                                invalid={convictionsRadiosShowError}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="hasConvictionsOrPending"
                              >
                                {t("moreAboutYouConvictionsHas")}
                              </Label>
                            </div>
                          </Col>
                          {formik.values.moreAboutYou
                            .hasConvictionsOrPending && (
                            <Col md={12} className="mt-2">
                              <Input
                                type="textarea"
                                name="moreAboutYou.criminalConvictionsDetails"
                                value={
                                  formik.values.moreAboutYou
                                    .criminalConvictionsDetails
                                }
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                rows={4}
                                placeholder={t(
                                  "moreAboutYouConvictionsDetailsPlaceholder"
                                )}
                                className="form-control"
                                invalid={moreAboutYouFieldShowError(
                                  "criminalConvictionsDetails"
                                )}
                              />
                              {moreAboutYouFieldShowError(
                                "criminalConvictionsDetails"
                              ) && (
                                <div className="invalid-feedback d-block">
                                  {
                                    formik.errors.moreAboutYou
                                      .criminalConvictionsDetails
                                  }
                                </div>
                              )}
                            </Col>
                          )}
                          {convictionsRadiosShowError && (
                            <Col md={12} className="mt-2">
                              <div className="invalid-feedback d-block">
                                {convictionsChoiceError}
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>

                      {/* About You and Your Work History */}
                      <div>
                        <h6 className="text-dark mb-2">
                          {t("moreAboutYouWorkHistoryTitle")}
                        </h6>
                        <p className="text-muted small mb-2">
                          {t("moreAboutYouWorkHistoryIntro")}
                        </p>
                        <Input
                          type="textarea"
                          name="moreAboutYou.aboutYouWorkHistory"
                          value={formik.values.moreAboutYou.aboutYouWorkHistory}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          rows={4}
                          placeholder={t("moreAboutYouWorkHistoryPlaceholder")}
                          className="form-control"
                          invalid={moreAboutYouFieldShowError(
                            "aboutYouWorkHistory"
                          )}
                        />
                        {moreAboutYouFieldShowError("aboutYouWorkHistory") && (
                          <div className="invalid-feedback d-block">
                            {formik.errors.moreAboutYou.aboutYouWorkHistory}
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>

                  {/* ===== Pension Auto-Enrolment ===== */}
                  <Card className="mb-3 form-card">
                    <CardHeader className="card-header-custom">
                      <h5 className="mb-0 card-header-title">
                        {t("pensionAutoEnrolmentTitle")}
                      </h5>
                    </CardHeader>
                    <CardBody className="card-body-custom">
                      <p className="text-muted mb-3">
                        {t("pensionAutoEnrolmentIntro")}
                      </p>
                      <Row>
                        <Col md={12} className="mb-3">
                          <div className="form-check">
                            <Input
                              id="pensionRemainEnrolled"
                              className="form-check-input"
                              type="radio"
                              name="pensionAutoEnrolmentChoice"
                              checked={
                                formik.values.pensionAutoEnrolment === true
                              }
                              onChange={() => setPensionChoice("remain")}
                              invalid={pensionRadiosShowError}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="pensionRemainEnrolled"
                            >
                              {t("pensionRemainEnrolled")}
                            </Label>
                          </div>
                        </Col>
                        <Col md={12} className="mb-3">
                          <div className="form-check">
                            <Input
                              id="pensionOptOut"
                              className="form-check-input"
                              type="radio"
                              name="pensionAutoEnrolmentChoice"
                              checked={
                                formik.values.pensionAutoEnrolment === false
                              }
                              onChange={() => setPensionChoice("optOut")}
                              invalid={pensionRadiosShowError}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="pensionOptOut"
                            >
                              {t("pensionOptOut")}
                            </Label>
                          </div>
                        </Col>
                        {pensionRadiosShowError && (
                          <Col md={12}>
                            <div className="invalid-feedback d-block">
                              {pensionChoiceError}
                            </div>
                          </Col>
                        )}
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ===== Work Preferences (Dynamic Sectors & Roles) ===== */}
                  <Card
                    className="mb-3"
                    style={{
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      borderRadius: "6px",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <CardHeader className="card-header-custom">
                      <h5 className="mb-0 card-header-title d-flex align-items-center gap-2 flex-wrap">
                        {t("workPreferencesIntro")}
                        <i
                          style={{ cursor: "help", fontWeight: "normal" }}
                          id="wo-h-work-section"
                          className="bx bx-info-circle"
                        ></i>
                        <UncontrolledTooltip target="wo-h-work-section">
                          {t("woTooltip_work_section")}
                        </UncontrolledTooltip>
                      </h5>
                    </CardHeader>
                    <CardBody className="card-body-custom">
                      <Row>
                        <Col md={12}>
                          {(formik.values.workPreferences.sectors || []).map(
                            (sec, idx) => {
                              const touched =
                                formik.touched.workPreferences?.sectors?.[
                                  idx
                                ] || {};
                              const errors =
                                formik.errors.workPreferences?.sectors?.[idx] ||
                                {};
                              const sectorSlug = sec.sectorSlug || "";
                              const roleOptions = getRoleOptions(sectorSlug);
                              const selectedRoles = sec.roles || [];

                              return (
                                <Card className="mb-3" key={idx}>
                                  <CardBody>
                                    <Row>
                                      <Col md={5} className="mb-3">
                                        <Label
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "normal",
                                            color: "#333333",
                                            marginBottom: "5px",
                                          }}
                                        >
                                          {t("sector")}{" "}
                                          <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                          type="select"
                                          value={sectorSlug}
                                          onChange={(e) =>
                                            setSectorSlug(idx, e.target.value)
                                          }
                                          onBlur={() =>
                                            touchNested(
                                              `workPreferences.sectors[${idx}].sectorSlug`
                                            )
                                          }
                                          disabled={sectorsLoading}
                                          className={
                                            touched?.sectorSlug &&
                                            errors?.sectorSlug
                                              ? "is-invalid"
                                              : ""
                                          }
                                        >
                                          <option value="">
                                            {sectorsLoading
                                              ? t("loading")
                                              : t("select")}
                                          </option>
                                          {getSectorOptionsForRow(idx).map(
                                            (opt) => (
                                              <option
                                                key={opt.value}
                                                value={opt.value}
                                                disabled={opt.disabled}
                                              >
                                                {opt.label}
                                              </option>
                                            )
                                          )}
                                        </Input>
                                        {touched?.sectorSlug &&
                                          errors?.sectorSlug && (
                                            <div className="invalid-feedback d-block">
                                              {errors?.sectorSlug}
                                            </div>
                                          )}
                                      </Col>

                                      <Col md={7} className="mb-3">
                                        <Label className="d-flex align-items-center justify-content-between">
                                          <span>
                                            {t("roles")}{" "}
                                            <span className="text-danger">
                                              *
                                            </span>
                                          </span>
                                        </Label>

                                        <Select
                                          isMulti
                                          isDisabled={
                                            !sectorSlug ||
                                            roleOptions.length === 0
                                          }
                                          options={roleOptions}
                                          value={roleOptions.filter((o) =>
                                            (selectedRoles || []).includes(
                                              o.value
                                            )
                                          )}
                                          onChange={(opts) => {
                                            const vals = Array.isArray(opts)
                                              ? opts.map((o) => o.value)
                                              : [];
                                            setRolesForSector(idx, vals);
                                          }}
                                          onBlur={() =>
                                            touchNested(
                                              `workPreferences.sectors[${idx}].roles`
                                            )
                                          }
                                          placeholder={
                                            !sectorSlug
                                              ? t("selectSectorFirst")
                                              : t("chooseRoles")
                                          }
                                          classNamePrefix="rs"
                                          menuPortalTarget={
                                            typeof document !== "undefined"
                                              ? document.body
                                              : null
                                          }
                                          styles={{
                                            menuPortal: (base) => ({
                                              ...base,
                                              zIndex: 9999,
                                            }),
                                            menu: (base) => ({
                                              ...base,
                                              backgroundColor: "#fff",
                                              zIndex: 9999,
                                            }),
                                            control: (base) => ({
                                              ...base,
                                              minHeight: 44,
                                              backgroundColor: "#fff",
                                              borderColor:
                                                touched?.roles && errors?.roles
                                                  ? "#dc3545"
                                                  : base.borderColor,
                                              boxShadow:
                                                touched?.roles && errors?.roles
                                                  ? "0 0 0 0.2rem rgba(220,53,69,.25)"
                                                  : base.boxShadow,
                                            }),
                                          }}
                                        />

                                        {touched?.roles && errors?.roles && (
                                          <div className="invalid-feedback d-block">
                                            {typeof errors?.roles === "string"
                                              ? errors?.roles
                                              : t("required")}
                                          </div>
                                        )}
                                      </Col>

                                      <Col
                                        md={12}
                                        className="d-flex justify-content-end"
                                      >
                                        {idx > 0 && (
                                          <Button
                                            color="outline-danger"
                                            size="sm"
                                            type="button"
                                            onClick={() => removeSector(idx)}
                                          >
                                            {t("removeSector")}
                                          </Button>
                                        )}
                                      </Col>
                                    </Row>
                                  </CardBody>
                                </Card>
                              );
                            }
                          )}
                          <div className="d-flex justify-content-start mb-3">
                            <Button
                              size="sm"
                              color="secondary"
                              type="button"
                              onClick={addSector}
                            >
                              <i className="bx bx-plus me-1" />
                              {t("addSector")}
                            </Button>
                          </div>
                        </Col>

                        {/* Preferred Radius */}
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard d-flex align-items-center gap-1 flex-wrap">
                            {t("preferredRadius")}{" "}
                            <span className="text-danger">*</span>
                            <i
                              style={{ cursor: "help" }}
                              id="wo-h-work-radius"
                              className="bx bx-info-circle"
                            ></i>
                            <UncontrolledTooltip target="wo-h-work-radius">
                              {t("woTooltip_work_radius")}
                            </UncontrolledTooltip>
                          </Label>
                          <Input
                            type="select"
                            name="workPreferences.preferredWorkRadius"
                            value={
                              formik.values.workPreferences
                                .preferredWorkRadius === "" ||
                              formik.values.workPreferences
                                .preferredWorkRadius == null
                                ? ""
                                : String(
                                    formik.values.workPreferences
                                      .preferredWorkRadius
                                  )
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              setNested(
                                "workPreferences.preferredWorkRadius",
                                v === "" ? "" : Number(v)
                              );
                            }}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.workPreferences
                                ?.preferredWorkRadius &&
                              !!formik.errors.workPreferences
                                ?.preferredWorkRadius
                            }
                          >
                            <option value="">{t("select")}</option>
                            {PREFERRED_WORK_RADIUS_OPTIONS.map((radius) => (
                              <option key={radius} value={radius}>
                                {radius}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>
                            {formik.errors.workPreferences?.preferredWorkRadius}
                          </FormFeedback>
                        </Col>

                        {/* Availability */}
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard d-flex align-items-center gap-1 flex-wrap">
                            {t("availability")}{" "}
                            <span className="text-danger">*</span>
                            <i
                              style={{ cursor: "help" }}
                              id="wo-h-work-avail"
                              className="bx bx-info-circle"
                            ></i>
                            <UncontrolledTooltip target="wo-h-work-avail">
                              {t("woTooltip_work_availability")}
                            </UncontrolledTooltip>
                          </Label>
                          <div className="d-flex gap-3 flex-wrap">
                            {AVAILABILITY_OPTIONS.map((opt) => (
                              <div className="form-check" key={opt}>
                                <Input
                                  id={`avail_${opt}`}
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={formik.values.workPreferences.availability.includes(
                                    opt
                                  )}
                                  onChange={() =>
                                    toggleInArray(
                                      "workPreferences.availability",
                                      opt
                                    )
                                  }
                                />
                                <Label
                                  className="form-check-label"
                                  htmlFor={`avail_${opt}`}
                                >
                                  {t(opt)}
                                </Label>
                              </div>
                            ))}
                          </div>
                          {formik.touched.workPreferences?.availability &&
                            formik.errors.workPreferences?.availability && (
                              <div className="invalid-feedback d-block">
                                {formik.errors.workPreferences?.availability}
                              </div>
                            )}
                        </Col>

                        {/* 48 hour opt-out */}
                        <Col md={12} className="mb-3">
                          <div className="form-check d-flex align-items-center flex-wrap gap-1">
                            <Input
                              id="optOut48"
                              className="form-check-input"
                              type="checkbox"
                              name="workPreferences.optOut48HourLimit"
                              checked={
                                formik.values.workPreferences.optOut48HourLimit
                              }
                              onChange={setChecked(
                                "workPreferences.optOut48HourLimit"
                              )}
                            />
                            <Label
                              className="form-check-label mb-0"
                              htmlFor="optOut48"
                            >
                              {t("optOut48")}
                            </Label>
                            <i
                              style={{ cursor: "help" }}
                              id="wo-h-work-optout48"
                              className="bx bx-info-circle"
                            ></i>
                            <UncontrolledTooltip
                              target="wo-h-work-optout48"
                              placement="top"
                            >
                              <div className="text-start wo-opt-out-48-tooltip">
                                <div className="fw-semibold mb-1">
                                  {t("woTooltip_optOut48_title")}
                                </div>
                                <p className="mb-1">
                                  {t("woTooltip_optOut48_p1")}
                                </p>
                                <p className="mb-1">
                                  {t("woTooltip_optOut48_p2")}
                                </p>
                                <p className="mb-1">
                                  {t("woTooltip_optOut48_p3")}
                                </p>
                                <p className="mb-0">
                                  {t("woTooltip_optOut48_p4")}
                                </p>
                              </div>
                            </UncontrolledTooltip>
                          </div>
                        </Col>

                        {/* What matters most */}
                        <Col md={12} className="mb-3">
                          <Label className="form-label-standard">
                            {t("whatMattersMost") ||
                              "What matters most to you?"}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <div className="d-flex gap-3 flex-wrap">
                            {WHAT_MATTERS_MOST_OPTIONS.map((opt) => (
                              <div className="form-check" key={opt}>
                                <Input
                                  id={`whatMatters_${opt.replace(/\s+/g, "_")}`}
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={formik.values.workPreferences.whatMattersMost.includes(
                                    opt
                                  )}
                                  onChange={() =>
                                    toggleInArray(
                                      "workPreferences.whatMattersMost",
                                      opt
                                    )
                                  }
                                  onBlur={() =>
                                    formik.setFieldTouched(
                                      "workPreferences.whatMattersMost",
                                      true
                                    )
                                  }
                                />
                                <Label
                                  className="form-check-label"
                                  htmlFor={`whatMatters_${opt.replace(
                                    /\s+/g,
                                    "_"
                                  )}`}
                                >
                                  {opt}
                                </Label>
                              </div>
                            ))}
                          </div>
                          {formik.touched.workPreferences?.whatMattersMost &&
                            formik.errors.workPreferences?.whatMattersMost && (
                              <div className="invalid-feedback d-block">
                                {formik.errors.workPreferences.whatMattersMost}
                              </div>
                            )}
                        </Col>

                        {/* Transport */}
                        <Col md={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("transport")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <div className="d-flex gap-3 flex-wrap">
                            {TRANSPORT_OPTIONS.map((tr) => (
                              <div className="form-check" key={tr}>
                                <Input
                                  id={`tr_${tr}`}
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={formik.values.workPreferences.transport.includes(
                                    tr
                                  )}
                                  onChange={() =>
                                    toggleInArray(
                                      "workPreferences.transport",
                                      tr
                                    )
                                  }
                                />
                                <Label
                                  className="form-check-label"
                                  htmlFor={`tr_${tr}`}
                                >
                                  {t(tr)}
                                </Label>
                              </div>
                            ))}
                          </div>
                          {formik.touched.workPreferences?.transport &&
                            formik.errors.workPreferences?.transport && (
                              <div className="invalid-feedback d-block">
                                {formik.errors.workPreferences?.transport}
                              </div>
                            )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ===== Certificates and compliance ===== */}
                  <div ref={complianceSectionRef}>
                    <Card className="mb-3 form-card">
                      <CardHeader className="card-header-custom">
                        <h5 className="mb-0 card-header-title">
                          {t("certificatesAndCompliance")}
                        </h5>
                      </CardHeader>
                      <CardBody className="card-body-custom">
                        <p className="mb-3">
                          {t("certificatesAndComplianceIntro")}
                        </p>
                        <div className="mb-4">
                          <h6 className="text-dark mb-1">
                            {t("certificatesDrivingDocsTitle")}
                          </h6>
                          <p className="text-muted small mb-3">
                            {t("certificatesDrivingDocsIntro")}
                          </p>
                          <Row>
                            <Col md={6} className="mb-3">
                              <Label className="d-flex align-items-center flex-wrap gap-1">
                                {t(
                                  "drivingLicenseFront",
                                  "Driving License Front"
                                )}
                              </Label>
                              <Input
                                type="file"
                                accept=".pdf,image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "rightToWork.drivingLicense",
                                    e.currentTarget.files?.[0] || null,
                                    FILE_VALIDATION.MAX_MB
                                  )
                                }
                                onBlur={() =>
                                  touchNested("rightToWork.drivingLicense")
                                }
                                invalid={
                                  formik.touched.rightToWork?.drivingLicense &&
                                  !!formik.errors.rightToWork?.drivingLicense
                                }
                              />
                              {formik.touched.rightToWork?.drivingLicense &&
                                formik.errors.rightToWork?.drivingLicense && (
                                  <div className="invalid-feedback d-block">
                                    {formik.errors.rightToWork.drivingLicense}
                                  </div>
                                )}
                            </Col>
                            <Col md={6} className="mb-3">
                              <Label className="d-flex align-items-center flex-wrap gap-1">
                                {t(
                                  "drivingLicenseBack",
                                  "Driving License Back"
                                )}
                              </Label>
                              <Input
                                type="file"
                                accept=".pdf,image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "rightToWork.drivingLicenseBack",
                                    e.currentTarget.files?.[0] || null,
                                    FILE_VALIDATION.MAX_MB
                                  )
                                }
                                onBlur={() =>
                                  touchNested("rightToWork.drivingLicenseBack")
                                }
                                invalid={
                                  formik.touched.rightToWork
                                    ?.drivingLicenseBack &&
                                  !!formik.errors.rightToWork
                                    ?.drivingLicenseBack
                                }
                              />
                              {formik.touched.rightToWork?.drivingLicenseBack &&
                                formik.errors.rightToWork
                                  ?.drivingLicenseBack && (
                                  <div className="invalid-feedback d-block">
                                    {
                                      formik.errors.rightToWork
                                        .drivingLicenseBack
                                    }
                                  </div>
                                )}
                            </Col>
                            <Col md={6} className="mb-3">
                              <Label>{t("dvlaShareCode")}</Label>
                              <Input
                                name="rightToWork.dvlaShareCode"
                                value={formik.values.rightToWork.dvlaShareCode}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={
                                  formik.touched.rightToWork?.dvlaShareCode &&
                                  !!formik.errors.rightToWork?.dvlaShareCode
                                }
                                placeholder={t("placeholderDvlaShareCode")}
                              />
                              <FormFeedback>
                                {formik.errors.rightToWork?.dvlaShareCode}
                              </FormFeedback>
                              <small className="text-muted d-block mt-1">
                                {t("dvlaShareCodeHelperPrefix")}{" "}
                                <a
                                  href={UK_DVLA_SHARE_CODE_URL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t("shareCodeHelperLinkText")}
                                </a>
                                .
                              </small>
                            </Col>
                            <Col md={6} className="mb-3">
                              <Label>{t("drivingLicenseExpiryDate")}</Label>
                              <Input
                                type="date"
                                name="rightToWork.drivingLicenseExpiryDate"
                                min={new Date().toISOString().split("T")[0]}
                                value={
                                  formik.values.rightToWork
                                    .drivingLicenseExpiryDate
                                }
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={
                                  formik.touched.rightToWork
                                    ?.drivingLicenseExpiryDate &&
                                  !!formik.errors.rightToWork
                                    ?.drivingLicenseExpiryDate
                                }
                              />
                              <FormFeedback>
                                {
                                  formik.errors.rightToWork
                                    ?.drivingLicenseExpiryDate
                                }
                              </FormFeedback>
                            </Col>
                            <Col md={6} className="mb-3">
                              <Label>{t("licenseNumber")}</Label>
                              <Input
                                name="rightToWork.licenseNumber"
                                value={formik.values.rightToWork.licenseNumber}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={
                                  formik.touched.rightToWork?.licenseNumber &&
                                  !!formik.errors.rightToWork?.licenseNumber
                                }
                                placeholder={t("placeholderLicenseNumber")}
                              />
                              <FormFeedback>
                                {formik.errors.rightToWork?.licenseNumber}
                              </FormFeedback>
                            </Col>
                          </Row>
                        </div>
                        {missingRoleRequiredCerts.length > 0 && (
                          <Alert color="info" className="mb-3">
                            <div className="fw-semibold mb-1">
                              {t("roleCertificatesSuggestedTitle")}
                            </div>
                            <div className="mb-2">
                              {t("roleCertificatesSuggestedBody")}
                            </div>
                            <ul
                              className="mb-2"
                              style={{ paddingLeft: "20px" }}
                            >
                              {missingRoleRequiredCerts.map((c) => (
                                <li key={c.slug}>{c.label}</li>
                              ))}
                            </ul>
                            <small className="text-muted">
                              {t("roleCertificatesContinueWithout")}
                            </small>
                          </Alert>
                        )}
                        <ul className="mb-3" style={{ paddingLeft: "20px" }}>
                          <li>{t("certificateTypeCV")}</li>
                          <li>{t("certificateTypeCertificates")}</li>
                          <li>{t("certificateTypeLicences")}</li>
                          <li>{t("certificateTypeIndustry")}</li>
                          <li>{t("certificateTypeMandatory")}</li>
                          <li>{t("certificateTypeHealthSafety")}</li>
                          <li>{t("certificateTypeDBS")}</li>
                          <li>{t("certificateTypeDriving")}</li>
                          <li>{t("certificateTypeAccreditation")}</li>
                          <li>{t("certificateTypeOther")}</li>
                        </ul>

                        {certsLoading ? (
                          <div className="text-center py-4">
                            <span className="spinner-border spinner-border-sm me-2" />
                            {t("loading")}
                          </div>
                        ) : (
                          <>
                            <Row>
                              {(formik.values.certificateEntries || []).map(
                                (entry, idx) => {
                                  const certOptions = (certsData || []).map(
                                    (cert) => ({
                                      value: cert.slug,
                                      label: toSentenceCase(cert.title),
                                    })
                                  );
                                  const selectedCert = certsData?.find(
                                    (c) => c.slug === entry?.certSlug
                                  );
                                  const isDbs = selectedCert?.hasServiceNumber;
                                  const hasCertFile = !!(
                                    entry?.file &&
                                    (entry.file instanceof File ||
                                      entry.file?.name)
                                  );
                                  const certTypeColMd = isDbs ? 3 : 5;
                                  const fileColMd = isDbs ? 4 : 5;
                                  const serviceNumberColMd = 4;
                                  const removeColMd = isDbs ? 1 : 2;
                                  const entryError =
                                    formik.errors.certificateEntries?.[idx];
                                  const entryTouched =
                                    formik.touched.certificateEntries?.[idx];

                                  return (
                                    <Col
                                      md={12}
                                      key={entry?.id ?? idx}
                                      className="mb-3"
                                    >
                                      <div
                                        className="p-3 rounded border certificate-entry-row"
                                        style={{ backgroundColor: "#f9f9f9" }}
                                      >
                                        <Row className="align-items-start">
                                          <Col
                                            md={certTypeColMd}
                                            className="mb-2"
                                          >
                                            <Label className="form-label-standard mb-1">
                                              {t("certificateTypeLabel")}{" "}
                                              <span className="text-muted">
                                                ({t("select")})
                                              </span>
                                            </Label>
                                            <Select
                                              options={certOptions}
                                              value={
                                                certOptions.find(
                                                  (o) =>
                                                    o.value === entry?.certSlug
                                                ) || null
                                              }
                                              onChange={(opt) => {
                                                const list = [
                                                  ...(formik.values
                                                    .certificateEntries || []),
                                                ];
                                                if (!list[idx]) return;
                                                list[idx] = {
                                                  ...list[idx],
                                                  certSlug: opt?.value ?? "",
                                                  serviceNumber: "",
                                                  file: null,
                                                };
                                                formik.setFieldValue(
                                                  "certificateEntries",
                                                  list
                                                );
                                              }}
                                              onBlur={() => {
                                                const list =
                                                  formik.values
                                                    .certificateEntries || [];
                                                const prev =
                                                  formik.touched
                                                    .certificateEntries;
                                                const arr = Array.isArray(prev)
                                                  ? [...prev]
                                                  : [];
                                                while (arr.length < list.length)
                                                  arr.push(false);
                                                arr[idx] = true;
                                                formik.setFieldTouched(
                                                  "certificateEntries",
                                                  arr
                                                );
                                              }}
                                              isClearable
                                              placeholder={t("select")}
                                              className={`react-select-container ${
                                                entryTouched && entryError
                                                  ? "is-invalid"
                                                  : ""
                                              }`}
                                              classNamePrefix="rs"
                                              menuPortalTarget={
                                                typeof document !== "undefined"
                                                  ? document.body
                                                  : null
                                              }
                                            />
                                          </Col>
                                          <Col md={fileColMd} className="mb-2">
                                            <Label className="form-label-standard mb-1">
                                              {t("uploadFile")}
                                            </Label>
                                            <Input
                                              type="file"
                                              accept=".pdf,image/*"
                                              innerRef={(el) => {
                                                const id = entry?.id;
                                                if (id == null) return;
                                                if (el)
                                                  certFileInputRefsRef.current[
                                                    id
                                                  ] = el;
                                                else
                                                  delete certFileInputRefsRef
                                                    .current[id];
                                              }}
                                              onChange={(e) => {
                                                const file =
                                                  e.target.files?.[0] ?? null;
                                                handleCertificateFile(
                                                  idx,
                                                  file,
                                                  e.target
                                                );
                                              }}
                                              className="form-control"
                                            />
                                            <div
                                              className="certificate-filename mt-1"
                                              style={{ minHeight: "20px" }}
                                            >
                                              {hasCertFile && (
                                                <div className="d-flex align-items-start gap-2">
                                                  <small
                                                    className="text-muted flex-grow-1 text-break"
                                                    title={entry.file.name}
                                                  >
                                                    {entry.file.name}
                                                  </small>
                                                  <Button
                                                    type="button"
                                                    color="link"
                                                    className="p-0 lh-1 text-danger flex-shrink-0"
                                                    title={t("remove")}
                                                    aria-label={t("remove")}
                                                    onClick={() =>
                                                      clearCertificateEntryFile(
                                                        idx,
                                                        entry?.id
                                                      )
                                                    }
                                                  >
                                                    <i className="mdi mdi-close fs-5" />
                                                  </Button>
                                                </div>
                                              )}
                                            </div>
                                          </Col>
                                          {isDbs && (
                                            <Col
                                              md={serviceNumberColMd}
                                              className="mb-2"
                                            >
                                              <Label className="form-label-standard mb-1">
                                                {t("serviceNumber")}
                                              </Label>
                                              <Input
                                                type="text"
                                                value={
                                                  entry?.serviceNumber ?? ""
                                                }
                                                onChange={(e) =>
                                                  handleDbsCertificateServiceNumberChange(
                                                    idx,
                                                    entry?.id,
                                                    e.target.value
                                                  )
                                                }
                                                placeholder={t(
                                                  "placeholderDbsServiceNumber",
                                                  "e.g. 001873344091"
                                                )}
                                                className="form-control"
                                              />
                                              <small className="text-muted d-block mt-1">
                                                {t(
                                                  "dbsServiceNumberHelperPrefix"
                                                )}{" "}
                                                <a
                                                  href={
                                                    UK_DBS_UPDATE_SERVICE_GOV_UK_URL
                                                  }
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  {t(
                                                    "dbsUpdateServiceCheckerLinkText"
                                                  )}
                                                </a>
                                                .
                                              </small>
                                            </Col>
                                          )}
                                          <Col
                                            md={removeColMd}
                                            className="mb-2 d-flex align-items-end flex-column"
                                          >
                                            {(formik.values.certificateEntries
                                              ?.length || 0) > 1 && (
                                              <Button
                                                type="button"
                                                color="outline-danger"
                                                size="sm"
                                                className="align-self-end"
                                                style={{ minHeight: "38px" }}
                                                onClick={() =>
                                                  removeCertificateEntry(idx)
                                                }
                                                title={t("remove")}
                                              >
                                                <i className="mdi mdi-minus" />
                                              </Button>
                                            )}
                                          </Col>
                                        </Row>
                                        {entryTouched && entryError && (
                                          <div className="invalid-feedback d-block mt-1">
                                            {typeof entryError === "object" &&
                                            entryError?.message
                                              ? entryError.message
                                              : entryError}
                                          </div>
                                        )}
                                      </div>
                                    </Col>
                                  );
                                }
                              )}
                            </Row>
                            <Row>
                              <Col md={12}>
                                <Button
                                  type="button"
                                  color="secondary"
                                  size="sm"
                                  outline
                                  onClick={addCertificateEntry}
                                >
                                  {t("addAnother")}
                                </Button>
                              </Col>
                            </Row>
                          </>
                        )}

                        <Row>
                          <Col md={12} className="mt-3 mb-1">
                            <div className="form-check">
                              <Input
                                id="hasInformationTrue"
                                className="form-check-input"
                                type="checkbox"
                                name="complianceSkills.hasInformationTrue"
                                checked={
                                  formik.values.complianceSkills
                                    .hasInformationTrue
                                }
                                onChange={setChecked(
                                  "complianceSkills.hasInformationTrue"
                                )}
                                onBlur={() =>
                                  touchNested(
                                    "complianceSkills.hasInformationTrue"
                                  )
                                }
                                invalid={
                                  formik.touched.complianceSkills
                                    ?.hasInformationTrue &&
                                  !!formik.errors.complianceSkills
                                    ?.hasInformationTrue
                                }
                              />
                              <Label
                                className="form-check-label d-flex align-items-center flex-wrap gap-1"
                                htmlFor="hasInformationTrue"
                              >
                                <span>
                                  {t("informationTrueConfirmation")}{" "}
                                  <span className="text-danger">*</span>
                                </span>
                                <i
                                  style={{ cursor: "help" }}
                                  id="wo-hint-confirm-info"
                                  className="bx bx-info-circle"
                                ></i>
                                <UncontrolledTooltip target="wo-hint-confirm-info">
                                  {t(
                                    "woTooltip_declaration_confirmInformation"
                                  )}
                                </UncontrolledTooltip>
                              </Label>
                            </div>
                            {formik.touched.complianceSkills
                              ?.hasInformationTrue &&
                              formik.errors.complianceSkills
                                ?.hasInformationTrue && (
                                <div className="invalid-feedback d-block">
                                  {
                                    formik.errors.complianceSkills
                                      .hasInformationTrue
                                  }
                                </div>
                              )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </div>

                  {/* ===== On-screening video ===== */}
                  <Card className="mb-3 form-card">
                    <CardHeader className="card-header-custom">
                      <h5 className="mb-0 card-header-title">
                        {t("onScreeningVideoTitle")}
                      </h5>
                    </CardHeader>
                    <CardBody className="card-body-custom">
                      <p className="text-muted mb-2">
                        {t("onScreeningVideoIntroLead")}
                      </p>
                      <p className="text-muted mb-2">
                        {t("onScreeningVideoIntroRecording")}
                      </p>
                      <p className="text-muted mb-3">
                        {t("onScreeningVideoIntroProfile")}
                      </p>
                      <div className="on-screening-inspiration mb-3">
                        <div
                          className="on-screening-inspiration__icon"
                          aria-hidden="true"
                        >
                          <i className="bx bx-bulb" />
                        </div>
                        <div className="on-screening-inspiration__content">
                          <p className="on-screening-inspiration__lead">
                            {t(
                              "onScreeningQuestionsTitle",
                              "Not sure what to say?"
                            )}
                          </p>
                          <p className="on-screening-inspiration__text">
                            {t(
                              "onScreeningQuestion",
                              "Imagine you're introducing yourself to a client. Why should they choose you?"
                            )}
                          </p>
                        </div>
                      </div>
                      <Row className="align-items-start">
                        <Col md={12} lg={6} className="mb-3">
                          <Label className="form-label-standard">
                            {t("onScreeningVideoPreviewLabel")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <div
                            className="rounded border overflow-hidden bg-dark"
                            style={{ aspectRatio: "16 / 10", minHeight: 200 }}
                          >
                            {onScreeningPreviewUrl ? (
                              <video
                                src={onScreeningPreviewUrl}
                                controls
                                playsInline
                                className="w-100 h-100"
                                style={{
                                  objectFit: "contain",
                                  maxHeight: 360,
                                }}
                              />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center text-white-50 small p-4 h-100">
                                {t("onScreeningNoVideoYet")}
                              </div>
                            )}
                          </div>
                          {formik.touched.onScreeningVideo &&
                            formik.errors.onScreeningVideo && (
                              <div className="invalid-feedback d-block">
                                {formik.errors.onScreeningVideo}
                              </div>
                            )}
                        </Col>
                        <Col md={12} lg={6} className="mb-3">
                          <Label className="form-label-standard d-block mb-2">
                            {t("onScreeningVideoActionsLabel")}
                          </Label>
                          <div className="d-flex flex-wrap gap-2 mb-3">
                            <Button
                              type="button"
                              color="primary"
                              size="sm"
                              onClick={openScreeningRecorder}
                            >
                              <i className="bx bx-video-recording me-1" />
                              {t("onScreeningOpenRecorder")}
                            </Button>
                            <label
                              className="btn btn-sm btn-outline-secondary mb-0"
                              style={{ cursor: "pointer" }}
                            >
                              <i className="bx bx-upload me-1" />
                              {t("onScreeningUploadVideo")}
                              <input
                                type="file"
                                accept="video/*,.webm,.mp4,.mov"
                                hidden
                                onChange={(e) => {
                                  const f = e.target.files?.[0] ?? null;
                                  e.target.value = "";
                                  if (!f) return;
                                  if (!isAcceptableOnScreeningVideoFile(f)) {
                                    toastr.error(
                                      t("onScreeningVideoInvalidType")
                                    );
                                    return;
                                  }
                                  if (
                                    !isUnder(
                                      f,
                                      FILE_VALIDATION.ONSCREENING_VIDEO_MAX_MB
                                    )
                                  ) {
                                    toastr.error(
                                      t("onScreeningVideoTooLarge", {
                                        n: FILE_VALIDATION.ONSCREENING_VIDEO_MAX_MB,
                                      })
                                    );
                                    return;
                                  }
                                  setOnScreeningVideoValue(f);
                                }}
                              />
                            </label>
                            {formik.values.onScreeningVideo && (
                              <Button
                                type="button"
                                color="outline-danger"
                                size="sm"
                                onClick={() => {
                                  setOnScreeningVideoValue(null);
                                }}
                              >
                                {t("onScreeningRemoveVideo")}
                              </Button>
                            )}
                          </div>
                          <p className="small text-muted mb-0">
                            {t("onScreeningVideoFootnote", {
                              n: FILE_VALIDATION.ONSCREENING_VIDEO_MAX_MB,
                            })}
                          </p>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  <div className="d-grid" style={{ paddingBottom: "20px" }}>
                    <button
                      className="btn btn-secondary btn-block"
                      type="submit"
                      disabled={
                        workerOnBoardingMutation.isPending ||
                        sectorsLoading ||
                        certsLoading
                      }
                    >
                      {workerOnBoardingMutation.isPending
                        ? t("verifyingEmail")
                        : t("verifyEmail")}
                    </button>
                  </div>
                </Form>
              </Col>
            </Row>
          </Container>
        </div>
      </div>

      <Modal
        isOpen={rtwIneligibleModalOpen}
        toggle={() => {
          setNested("rightToWork.holdsLegalRightToWorkUk", "");
          setRtwIneligibleModalOpen(false);
        }}
        centered
      >
        <ModalHeader
          toggle={() => {
            setNested("rightToWork.holdsLegalRightToWorkUk", "");
            setRtwIneligibleModalOpen(false);
          }}
        >
          {t("rightToWork")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">{t("rtwNoLegalRightModalMessage")}</p>
        </ModalBody>
        <ModalFooter className="d-flex gap-2 flex-wrap">
          <Button
            color="secondary"
            type="button"
            onClick={() => {
              setNested("rightToWork.holdsLegalRightToWorkUk", "");
              setRtwIneligibleModalOpen(false);
            }}
          >
            {t("rtwGoBack")}
          </Button>
          <Button
            color="primary"
            type="button"
            onClick={() => {
              setRtwIneligibleModalOpen(false);
              props.router.navigate("/work-interest");
            }}
          >
            {t("rtwExit")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={roleCertModalOpen}
        toggle={() => setRoleCertModalOpen(false)}
        centered
      >
        <ModalHeader toggle={() => setRoleCertModalOpen(false)}>
          {t("roleCertificatesSuggestedTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">{t("roleCertificatesSuggestedBody")}</p>
          {roleCertModalLabels.length > 0 && (
            <ul className="mb-0">
              {roleCertModalLabels.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            type="button"
            onClick={() => {
              dismissedRoleCertFpRef.current = roleCertRequiredFpRef.current;
              setRoleCertModalOpen(false);
            }}
          >
            {t("roleCertificateUnderstood")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={drivingDocsModalOpen}
        toggle={() => {
          dismissedDrivingDocsFpRef.current = drivingDocsRequiredFpRef.current;
          setDrivingDocsModalOpen(false);
        }}
        centered
      >
        <ModalHeader
          toggle={() => {
            dismissedDrivingDocsFpRef.current =
              drivingDocsRequiredFpRef.current;
            setDrivingDocsModalOpen(false);
          }}
        >
          {t("drivingDocsPopupTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">{t("drivingDocsPopupBody")}</p>
          {drivingDocsModalLabels.length > 0 && (
            <ul className="mb-0">
              {drivingDocsModalLabels.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            type="button"
            onClick={() => {
              dismissedDrivingDocsFpRef.current =
                drivingDocsRequiredFpRef.current;
              setDrivingDocsModalOpen(false);
            }}
          >
            {t("drivingDocsPopupAcknowledge")}
          </Button>
          <Button
            color="secondary"
            type="button"
            onClick={() => {
              dismissedDrivingDocsFpRef.current =
                drivingDocsRequiredFpRef.current;
              setDrivingDocsModalOpen(false);
            }}
          >
            {t("drivingDocsPopupContinueWithout")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* On-screening video recorder */}
      <Modal
        isOpen={screeningRecorderOpen}
        toggle={() => {
          if (screeningRecording) {
            toastr.warning(t("onScreeningStopBeforeClose"));
            return;
          }
          cancelScreeningRecorder();
        }}
        size="lg"
        centered
        backdrop={screeningRecording ? "static" : true}
        keyboard={!screeningRecording}
      >
        <ModalHeader
          toggle={() => {
            if (screeningRecording) {
              toastr.warning(t("onScreeningStopBeforeClose"));
              return;
            }
            cancelScreeningRecorder();
          }}
        >
          {t("onScreeningRecorderModalTitle")}
        </ModalHeader>
        <ModalBody>
          <div
            className="position-relative rounded overflow-hidden bg-dark"
            style={{ minHeight: 300 }}
          >
            <video
              ref={screeningRecordVideoRef}
              autoPlay
              playsInline
              muted
              className="w-100 d-block"
              style={{
                minHeight: 300,
                maxHeight: 420,
                objectFit: "cover",
              }}
            />
            <div
              className="position-absolute bottom-0 start-0 end-0 px-3 py-2"
              style={{
                background: screeningQuestionsHighContrast
                  ? "linear-gradient(0deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.55) 70%, transparent 100%)"
                  : "linear-gradient(0deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.28) 55%, transparent 100%)",
              }}
            >
              <div className="d-flex justify-content-between align-items-start mb-1 gap-2 flex-wrap">
                <div
                  className="on-screening-inspiration-overlay d-flex align-items-start gap-2"
                  style={{
                    opacity: screeningQuestionsHighContrast ? 1 : 0.85,
                  }}
                >
                  <i
                    className="bx bx-bulb flex-shrink-0 mt-1"
                    aria-hidden="true"
                  />
                  <div>
                    <p className="on-screening-inspiration-overlay__lead">
                      {t(
                        "onScreeningQuestionsTitle",
                        "Not sure what to say?"
                      )}
                    </p>
                    <p className="on-screening-inspiration-overlay__text">
                      {t(
                        "onScreeningQuestion",
                        "Imagine you're introducing yourself to a client. Why should they choose you?"
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  color="light"
                  className="py-0 px-2 flex-shrink-0"
                  onClick={() => setScreeningQuestionsHighContrast((v) => !v)}
                >
                  {screeningQuestionsHighContrast
                    ? t("onScreeningQuestionsDim")
                    : t("onScreeningQuestionsHighlight")}
                </Button>
              </div>
            </div>
          </div>
          {screeningRecording && (
            <div className="mt-2">
              <span className="badge bg-danger text-white">
                {t("onScreeningRecording")}
              </span>
            </div>
          )}
          <p className="small text-muted mt-2 mb-0">
            {t("onScreeningRecorderHint")}
          </p>
        </ModalBody>
        <ModalFooter className="d-flex flex-wrap gap-2">
          <Button
            type="button"
            color="secondary"
            onClick={() => {
              if (screeningRecording) {
                toastr.warning(t("onScreeningStopBeforeClose"));
                return;
              }
              cancelScreeningRecorder();
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            color="primary"
            disabled={screeningRecording}
            onClick={startScreeningRecording}
          >
            <i className="bx bx-play-circle me-1" />
            {t("onScreeningRecordStart")}
          </Button>
          <Button
            type="button"
            color="danger"
            outline
            disabled={!screeningRecording}
            onClick={stopScreeningRecording}
          >
            <i className="bx bx-stop-circle me-1" />
            {t("onScreeningRecordStop")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Webcam Modal */}
      <Modal isOpen={webcamModal} toggle={closeWebcam} size="md" centered>
        <ModalHeader toggle={closeWebcam}>
          {t("takeSelfieWithWebcam")}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-center">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                maxWidth: "500px",
                borderRadius: "8px",
                backgroundColor: "#000",
                transform: "scaleX(-1)", // Mirror the video for selfie
              }}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeWebcam}>
            {t("cancel")}
          </Button>
          <Button color="primary" onClick={capturePhoto}>
            <i className="bx bx-camera me-1"></i>
            {t("capturePhoto")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

WorkerOnBoarding.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
};

export default withRouter(withTranslation()(WorkerOnBoarding));
